// Seoul, "Ginkgo night under Namsan" (mid-November, crisp and clear). Looking south-south-east from under the eave
// of a hanok near Sungnyemun: N Seoul Tower stands on the forested ridge of Namsan and slowly changes colour while
// the cable car climbs to it; Sungnyemun, the great south gate, glows gold on its island among blue and green
// buses, orange taxis and a delivery scooter; Seoullo's blue walkway runs behind it; the market's neon crowds the
// street; an orange pojangmacha tent steams on the sidewalk; a street ginkgo sheds its yellow fans at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex, mix,
  makeCanvas, rect, makeHalo, drawHalo, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
// Facing SSE from the north-west of the gate: the tower on Namsan rises behind and to the left of Sungnyemun
// (Namdaemun Market between them), Seoullo 7017 runs behind the gate towards Seoul Station on the right.
const GROUND = 206;                                   // ground line of the gate's island
const FAR_ROAD = 202;                                 // the road behind the island (small lights only)
const ROAD = { y0: 208, y1: 231 };                    // the road in front of the gate
const WALK = 235;                                     // the near sidewalk starts here (curb just above)
const TW = { c: 172, base: 125, podB: 81, podT: 67, drum: 62, ant: 22 };   // N Seoul Tower (4 px shaft: c-2..c+1)
const PEAK = { x: 171, y: 127 };                      // Namsan's summit under the tower
const GATE = { cx: 292, g: GROUND };                  // Sungnyemun, centre column and ground line
const MOON = { x: 336, y: 38 };
const BB = { x: 196, y: 100, w: 48, h: 20 };          // the rooftop billboard's face (the site paints its pitch here)
const BBT = { x: 190, w: 60, top: 128 };              // the glass tower it stands on
const BB_LAMPS = [8, 24, 40];                         // floodlamps under the face (x offsets)
const BB_FLICK = [[88.2, 89.5], [201.4, 202.3]];      // the middle floodlamp stutters
const TENT = { x0: 8, x1: 122, ridge: 176, eave: 186, base: 251 };
const LAMP = { post: 402, x: 386, y: 134 };           // street lamp post and the light under its head
const TREE = { x: 446, crownB: 158 };                 // ginkgo trunk and the bottom of its crown
const TIP = { x: 124, y: 11 };                        // the upturned corner of the hanok eave (top left)
const CABLE = [[60, 170], [160, 130]];                // Namsan cable car: lower (hidden) and upper station

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (col == null || i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(ax, ay, bx, by, col) {
      const n = Math.max(1, Math.round(Math.max(Math.abs(bx - ax), Math.abs(by - ay))));
      for (let s = 0; s <= n; s++) this.set(lerp(ax, bx, s / n), lerp(ay, by, s / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** posterised ramp: flat bands with a narrow dither seam (a printed poster, not a gradient) */
function pickFlat(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
const mixRGB = (a, b, t) => [Math.round(lerp(a[0], b[0], t)), Math.round(lerp(a[1], b[1], t)), Math.round(lerp(a[2], b[2], t))];
const mixHex = (a, b, t) => mixRGB(C(a), C(b), t);
const toHex = c => '#' + c.map(v => v.toString(16).padStart(2, '0')).join('');
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** draw a glyph given as rows of '#'/'.' into a pix buffer */
function glyphP(p, g, x, y, col) { g.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') p.set(x + i, y + j, col); }); }
/** additive sprite draw (pre-rendered light shapes) */
function addBlit(c, x, y, a = 1) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(c, Math.round(x), Math.round(y));
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}

/* ================= Invented Hangul-like glyphs (no real words) ================= */
// Circles, boxes, bars with ticks, like Hangul, but always with a part Hangul doesn't have or can't put there: the
// vowel before the initial, a mirrored initial (a flipped giyeok, nieun or rieul), a vowel crossed both ways. So no
// block reads as a real syllable, and a row of them never spells a word.
const G6 = [
  ['#...#.', '#..#.#', '##..#.', '#.....', '.....#', '######'],
  ['#..###', '#..#.#', '#..###', '#.....', '######', '......'],
  ['#..###', '#..#..', '##.###', '#....#', '#..###', '......'],
  ['###.#.', '#..###', '#...#.', '#...#.', '......', '#.#.#.'],
  ['.#..#.', '#.##.#', '.#..#.', '......', '##.###', '.#...#'],
  ['#.###.', '#.#.#.', '#.###.', '##.#..', '#.#.#.', '#..#..'],
  ['#..#.#', '#..#.#', '#.##.#', '#.....', '#.####', '#....#'],
  ['###..#', '#....#', '###.##', '..#..#', '###..#', '......'],
];
const G5 = [
  ['###.#', '#...#', '#..##', '....#', '#####'], ['#..#.', '#.#.#', '##.#.', '#....', '#####'],
  ['#.###', '#.#.#', '#.###', '#....', '#####'], ['.#.#.', '#.#.#', '.#.#.', '..#..', '#####'],
  ['###.#', '#...#', '#...#', '.....', '#.#.#'], ['#.#.#', '#.###', '#.#.#', '#.###', '.....'],
  ['..#.#', '..#.#', '###.#', '.....', '#####'], ['#.#.#', '#..#.', '#.#.#', '#....', '#####'],
];

/* ================= Sky (crisp November night, violet city glow low down) ================= */
const SKY = ['#04061a', '#060920', '#080c26', '#0b0f2c', '#0e1233', '#12153a', '#171841', '#1d1b48', '#241e4f',
  '#2c2256', '#35265c', '#402a62', '#4c2f66', '#593569', '#673b6b', '#75426c', '#83496c'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 212), 1.45) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - 205) / 260, (y - 205) / 88)) * 0.2;           // market and street glow
    v += Math.max(0, 1 - Math.hypot((i - TW.c) / 60, (y - 80) / 70)) * 0.05;          // the tower's own wash
    v -= Math.max(0, 1 - Math.hypot((i - MOON.x) / 80, (y - MOON.y) / 56)) * 0.05;
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(31);
  while (STARS.length < 70) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 118);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16 || (x < TIP.x + 6 && y < 46)) continue;
    STARS.push({ x, y, k: 18 + Math.floor(r() * 60), ph: r(), b: 0.2 + r() * r() * 0.8, big: r() < 0.06 });
  }
}
/** waxing gibbous moon, cool white with faint maria; the dark limb on the left shows a little earthshine */
function renderMoon() {
  const r = 7, s = 17, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - 8, dy = y - 8, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    const term = -0.45 * Math.sqrt(Math.max(0, r * r - dy * dy));              // elliptical terminator
    let col;
    if (dx < term - 0.6) col = dd > r - 0.9 ? '#302e62' : '#24224e';
    else if (dx < term + 0.6) col = '#9a9cc8';
    else {
      col = dd > r - 0.9 ? '#d4d6f2' : '#f4f2ff';
      const m = vnoise(i / 2.4 + 3, y / 2.4, 7);
      if (m > 0.6 && dd < r - 0.9) col = '#dcdcf2'; if (m > 0.72 && dd < r - 0.9) col = '#c8cae8';
    }
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= Clouds (a thin band of cirrus lit by the moon, tileable; the night is otherwise clear) ================= */
const CLOUD_DEFS = [
  { seed: 17, y: 34, h: 30, banks: 3, k: 1, rx: [10, 26], ry: [1.2, 2.6],
    pal: { top: '#3e3a78', body: '#27255a', core: '#211f50', bot: '#34306a', bot2: '#2a275e' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 80 + r() * 140, base = h * (0.45 + r() * 0.3);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = lerp(def.rx[0], def.rx[1], r()), ry = lerp(def.ry[0], def.ry[1], r());
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.6;
      if (v > best) best = v;
    }
    best += 0.3 * Math.sin(TAU * 13 * x / W + y * 0.9 + def.seed) * Math.sin(TAU * 7 * x / W - y * 0.4)   // streaky, torn
      + 0.35 * (vnoise(x / 6, y / 1.5, def.seed) - 0.5);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.36, v = D[y * W + x];
    if (v + th <= 0.12) continue;
    let col;
    if (get(x, y - 1) + th <= 0.12) col = P.top;
    else if (get(x, y + 1) + th <= 0.12) col = P.bot;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Far city beyond Namsan's shoulders (hazy, tiny windows) ================= */
function renderFarCity() {
  const p = pix(W, H), r = rng(23), wins = [];
  for (const [xa, xb, lo, hi] of [[0, 70, 156, 170], [300, 480, 146, 166]]) {
    let x = xa;
    while (x < xb) {
      const bw = 4 + Math.floor(r() * 11), top = lo + Math.floor(r() * (hi - lo));
      for (let y = top; y < 200; y++) for (let i = x; i < Math.min(xb, x + bw); i++)
        p.set(i, y, y === top ? '#2a2450' : i === x ? '#221d44' : '#1b1738');
      for (let wy = top + 2; wy < 190; wy += 3) for (let wx = x + 1; wx < x + bw - 1; wx += 2)
        if (r() < 0.22) wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 6), off: r(), col: r() < 0.6 ? '#9a7a78' : '#7a80b8' });
      x += bw + (r() < 0.3 ? 1 : 0);
    }
  }
  return { ...trim(p), wins };
}

/* ================= Namsan (forested ridge, trail lamps, cable car ropes, the tower's plaza) ================= */
function hillTop(x) {
  let y;
  if (x <= PEAK.x) y = PEAK.y + 43 * Math.pow((PEAK.x - x) / PEAK.x, 1.55);
  else y = PEAK.y + 40 * Math.pow((x - PEAK.x) / (W - PEAK.x), 1.3) - 3.5 * Math.exp(-(((x - 352) / 28) ** 2));
  if (Math.abs(x - PEAK.x) < 16) y -= 1.5 * (1 - Math.abs(x - PEAK.x) / 16);           // the plaza terrace on top
  return y + (vnoise(x / 4.5, 0, 3) - 0.5) * 3.2 + (vnoise(x / 1.8, 2, 5) - 0.5) * 1.4;
}
const HILL_COOL = ['#05080f', '#080c16', '#0b111d', '#0f1624', '#131c2c', '#192335'];
const HILL_WARM = ['#0b0a14', '#110e1a', '#171320', '#1e1726', '#261c2c'];
const TRAIL = [];                                     // the city-wall trail lamps along the western ridge
const ROADLAMPS = [];                                 // Sowol-ro lamps on the northern slope
function renderHill() {
  const p = pix(W, H), tops = [];
  for (let x = 0; x < W; x++) tops[x] = Math.round(hillTop(x));
  for (let x = 0; x < W; x++) for (let y = tops[x]; y < 206; y++) {
    const dep = y - tops[x], warm = smooth(146, 196, y) + (hash(x, y, 4) - 0.5) * 0.3;
    const crown = vnoise(x / 3.1, y / 2.5, 11), crown2 = vnoise(x / 1.6, y / 1.4, 12);
    const up = crown - vnoise(x / 3.1, (y + 1.3) / 2.5, 11);                               // crowns lit from above
    let v = 0.34 + (crown - 0.5) * 0.7 + (crown2 - 0.5) * 0.25 - up * 1.6 - Math.min(0.2, dep * 0.006);
    v += Math.max(0, 1 - Math.hypot((x - PEAK.x) / 40, (y - PEAK.y) / 22)) * 0.35;         // the tower's light on the summit
    let col = warm > bayer(x, y) * 1.4 ? pick(HILL_WARM, v * 0.9, x, y) : pick(HILL_COOL, v, x, y);
    if (dep === 0) col = x > PEAK.x ? '#263047' : '#20283c';                                 // moonlit canopy rim
    if (dep === 1 && hash(x, 1, 9) < 0.5) col = '#172033';
    p.set(x, y, col);
  }
  // the tower's plaza (a low round building) and the cable car's upper station beside it
  for (let y = 119; y < 128; y++) for (let i = 160; i <= 184; i++) {
    const e = Math.abs(i - 172) / 12.5;
    if (y < 119 + Math.round(e * e * 3)) continue;
    let col = y === 119 + Math.round(e * e * 3) ? '#8c86b0' : i < 168 ? '#3a3858' : '#2c2a46';
    if ((y === 122 || y === 125) && (i & 1) && e < 0.9) col = y === 122 ? '#ffe2a6' : '#f0c890';
    p.set(i, y, col);
  }
  p.fill(152, 126, 8, 4, '#2a2844'); p.fill(152, 126, 8, 1, '#6a6488'); p.set(154, 128, '#ffd89a'); p.set(157, 128, '#ffd89a');
  // cable car ropes (the cabins are drawn per frame) and one pylon
  const [[ax, ay], [bx, by]] = CABLE;
  for (const o of [0, 2]) p.line(ax, ay + o, bx - 2, by + o - 1, o ? '#2a3044' : '#343a52');
  const px0 = 104, py0 = Math.round(lerp(ay, by, (px0 - ax) / (bx - ax)));
  for (let y = py0; y < py0 + 9; y++) { p.set(px0, y, '#2e3448'); if (y > py0 + 2) p.set(px0 + ((y - py0) & 1 ? 1 : -1), y, '#252a3c'); }
  p.fill(px0 - 2, py0, 5, 1, '#3a4058');
  // the city-wall trail on the western ridge and Sowol-ro on the northern slope: strings of small warm lamps
  for (let x = 188; x < 336; x += 6 + (x % 5 === 0 ? 1 : 0)) {
    const y = tops[x] + 3 + Math.round(1.5 * Math.sin(x * 0.19));
    TRAIL.push({ x, y, id: TRAIL.length });
    p.set(x, y, '#c89a62'); p.set(x - 1, y, '#3a2c2a'); p.set(x + 1, y, '#3a2c2a');
  }
  for (let x = 14; x < 160; x += 7) {
    const y = Math.round(164 - (x - 14) * 0.13 + 3 * Math.sin((x - 14) / 22));
    if (y < tops[x] + 5) continue;
    ROADLAMPS.push({ x, y, id: ROADLAMPS.length });
    p.set(x, y, '#e8b878');
  }
  return { ...trim(p), tops };
}

/* ================= N Seoul Tower (white shaft, observation pod, steel mast; LED wash changes colour) ================= */
const WASH = ['#b0a4ff', '#5c8eff', '#3fd6c2', '#ff7ab4'];          // lavender, blue, teal, rose: one per minute
const WASH_LEN = 60, WASH_FADE = 7;
function renderTower(wash) {
  const Wc = C(wash), p = pix(28, 110), ox = TW.c - 14, oy = 18;
  const put = (x, y, col) => p.set(x - ox, y - oy, col);
  const dark = mixRGB([16, 14, 36], Wc, 0.28), body = mixRGB([236, 232, 246], Wc, 0.48), shell = mixRGB([244, 240, 250], Wc, 0.3);
  const shade = (c, k) => mixRGB(dark, c, k);
  // shaft: uplit from the plaza and from spots just under the pod, lit side on the left
  for (let y = TW.podB + 1; y <= TW.base; y++) {
    const I = 0.5 + 0.42 * smooth(104, 124, y) + 0.36 * smooth(93, 83, y);
    const flare = y >= TW.base - 5, x0 = flare ? TW.c - 3 : TW.c - 2, x1 = flare ? TW.c + 2 : TW.c + 1;
    for (let x = x0; x <= x1; x++) {
      const k = x - x0, n = x1 - x0;
      const sh = k === 0 ? 0.92 : k === 1 ? 1.05 : k === n ? 0.42 : 0.72;
      put(x, y, shade(body, clamp(sh * I, 0, 1)));
    }
    if (y % 9 === 3) put(TW.c - 1, y, shade(body, 0.6));                                    // faint ring joints
  }
  // the pod: under-cone, restaurant band (warm), divider, observation band (cool), roof, upper deck, cap
  const rows = [[81, 8, 'cone'], [80, 12, 'cone'], [79, 14, 'cone'], [78, 16, 'warm'], [77, 16, 'warm'], [76, 16, 'warm'],
    [75, 16, 'rim'], [74, 16, 'shell'], [73, 16, 'cool'], [72, 16, 'cool'], [71, 16, 'cool'], [70, 16, 'rim'],
    [69, 12, 'shell'], [68, 10, 'deck'], [67, 8, 'shell']];
  for (const [y, w, kind] of rows) {
    const x0 = TW.c - w / 2, x1 = TW.c + w / 2 - 1;
    for (let x = x0; x <= x1; x++) {
      const k = (x - x0) / (w - 1), curve = 1 - Math.abs(k - 0.35) * 0.9;                // round: lit left of centre
      let col;
      if (kind === 'cone') col = shade(shell, 0.45 + 0.55 * curve * (y === 79 ? 1 : 0.8));
      else if (kind === 'rim') col = shade(shell, 0.55 + 0.45 * curve);
      else if (kind === 'shell') col = shade(shell, 0.4 + 0.5 * curve);
      else if (kind === 'deck') col = (x - x0) % 3 === 1 ? shade(shell, 0.5) : mixRGB([255, 236, 190], [150, 120, 90], 1 - curve);
      else {
        const mull = (x - x0) % 3 === 0 || x === x0 || x === x1, warm = kind === 'warm';
        const glass = warm ? mixRGB([255, 222, 150], [160, 110, 70], 0.9 - curve) : mixRGB([226, 238, 255], [110, 120, 170], 0.9 - curve);
        col = mull ? shade(shell, 0.35 + 0.3 * curve) : glass;
        if (!mull && warm && y === 78) col = mixRGB(glass, [255, 246, 220], 0.4);
      }
      put(x, y, col);
    }
  }
  // drum with a lit ring, then the steel mast: lattice, two platforms, the needle
  for (let y = TW.drum; y < TW.podT; y++) for (let x = TW.c - 3; x <= TW.c + 2; x++) {
    const k = x - (TW.c - 3);
    put(x, y, y === 64 ? mixRGB(Wc, [255, 255, 255], 0.35) : shade(shell, k < 2 ? 0.8 : k < 4 ? 0.62 : 0.4));
  }
  const steel = mixRGB([214, 214, 230], Wc, 0.18);
  for (let y = TW.ant; y < TW.drum; y++) {
    let x0 = TW.c - 1, x1 = TW.c;
    if (y >= 50) { x0 = TW.c - 2; x1 = TW.c + 1; }
    if (y < 28) x0 = x1 = TW.c - 1;
    for (let x = x0; x <= x1; x++) {
      const lat = y >= 50 ? ((x + y) & 1) : (y & 1) && x === x1;
      put(x, y, shade(steel, lat ? 0.45 : x === x0 ? 1 : 0.72));
    }
  }
  for (let x = TW.c - 3; x <= TW.c + 2; x++) put(x, 49, shade(steel, x < TW.c ? 0.95 : 0.6));
  for (let x = TW.c - 2; x <= TW.c + 1; x++) put(x, 36, shade(steel, x < TW.c ? 0.9 : 0.55));
  return { c: p.done(), x: ox, y: oy };
}

/* ================= Glass towers (Hoehyeon on the left, the station side on the right; floors toggle) ================= */
const TOWERS = [
  { x: 112, w: 26, top: 150, crown: 'flat', cool: 0.4 },
  { x: BBT.x, w: BBT.w, top: BBT.top, crown: 'flat', cool: 0.75 },              // carries the billboard
  { x: 346, w: 20, top: 104, crown: 'band', cool: 0.8, blink: true, band: '#8ec8ff' },
  { x: 364, w: 16, top: 82, crown: 'slant', cool: 0.6, blink: true },
  { x: 381, w: 26, top: 116, crown: 'flat', cool: 0.35 },
  { x: 404, w: 24, top: 94, crown: 'band', cool: 0.7, blink: true, band: '#c2b8ff' },
  { x: 428, w: 52, top: 122, crown: 'flat', cool: 0.5 },
];
const tTop = (b, i) => b.crown === 'slant' ? b.top + Math.round((b.w - 1 - i) * 0.7) : b.top;
function renderTowers() {
  const p = pix(W, H), lit = pix(W, H), r = rng(41), segs = [], blinks = [], bands = [];
  const warm = ['#ffd694', '#ffc878', '#ffe2b0'], cool = ['#cfe2ff', '#b4ccff', '#e6f0ff'];
  for (const b of TOWERS) {
    for (let i = 0; i < b.w; i++) {
      const top = tTop(b, i), gx = b.x + i;
      for (let y = top; y < 206; y++) {
        const g = clamp((y - top) / 90, 0, 1);                                               // glass: sky-lit high, dark low
        let col = pick(['#141a33', '#171e3a', '#1b2342', '#212a4c'], 0.75 - g * 0.6 + ((i % 4 === 0) ? -0.25 : 0), gx, y);
        if (i === 0) col = '#2c3660'; else if (i === b.w - 1) col = '#0e1226';
        if (y === top) col = '#46507e';
        p.set(gx, y, col);
      }
    }
    if (b.crown === 'slant') for (let i = 0; i < b.w; i++) p.set(b.x + i, tTop(b, i), '#b8c8f0');
    if (b.crown === 'band') bands.push({ x: b.x + 1, y: b.top + 2, w: b.w - 2, col: b.band });
    if (b.blink) blinks.push([b.x + 2, tTop(b, 2) - 1], [b.x + b.w - 3, tTop(b, b.w - 3) - 1]);
    for (let y = b.top + 5; y < 200; y += 3) {
      let x = b.x + 2;
      while (x < b.x + b.w - 2) {
        const len = Math.min(b.x + b.w - 2 - x, 3 + Math.floor(r() * 9));
        const col = (r() < b.cool ? cool : warm)[Math.floor(r() * 3)];
        let any = false;
        for (let k = 0; k < len; k += 2) {
          const gx = x + k;
          if (y < tTop(b, gx - b.x) + 4) continue;
          p.set(gx, y, '#0c0f22'); lit.set(gx, y, col); any = true;
        }
        if (any) segs.push({ id: segs.length, x, y, w: len, n: 2 + Math.floor(r() * 7), p: 0.35 + r() * 0.4, off: r() });
        x += len;
      }
    }
  }
  return { ...trim(p), lit: lit.done(), segs, blinks, bands };
}

/* ================= The rooftop billboard (floodlit, on a steel frame over the glass tower) ================= */
// Its own ad is an invented fried-chicken place, KKOKKO CHICKEN: a golden drumstick on a red sunburst, steam rising.
const BB_RED = ['#4a0c14', '#761420', '#a01c26', '#c82a2c', '#e44632', '#ff6c3e'];
const DRUM = { x: 8, y: 8 };                                   // drumstick meat centre inside the face
function bbLight(i, j, dark) {
  let v = 0;
  BB_LAMPS.forEach((lx, k) => { if (k !== dark) v += Math.max(0, 1 - Math.hypot((i - lx) / 12, (BB.h - j) / 34)) ** 1.2; });
  const a = Math.atan2(j - DRUM.y, i - DRUM.x), d = Math.hypot(i - DRUM.x, j - DRUM.y);
  if (d < 13 && Math.cos(a * 8) > 0.2) v += 0.35 * (1 - d / 13);                          // printed sunburst
  return 0.1 + 0.62 * Math.min(1.15, v);
}
function renderBBBack(dark) {
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) p.set(i, j, pickFlat(BB_RED, bbLight(i, j, dark), i, j));
  return p.done();
}
function renderBBTop() {                                       // drumstick + lettering (transparent elsewhere), and a shine mask
  const p = pix(BB.w, BB.h), m = pix(BB.w, BB.h);
  const MEAT = ['#5a2a0e', '#8e4a16', '#c07a24', '#e6a83c', '#ffd67a'];
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < 16; i++) {
    const dx = (i - DRUM.x) / 5.6, dy = (j - DRUM.y) / 5, dd = dx * dx + dy * dy;
    if (dd > 1 || (dd > 0.8 && hash(i, j, 3) < 0.4)) continue;
    const lv = 0.55 - dx * 0.25 - dy * 0.35 + (hash(i, j, 5) - 0.5) * 0.45 - dd * 0.2;   // crunchy crust, lit top left
    p.set(i, j, pick(MEAT, lv, i, j)); m.set(i, j, '#ffffff');
  }
  for (const [i, j, c] of [[4, 12, '#f4ead0'], [3, 13, '#f4ead0'], [3, 14, '#e8dcc0'], [2, 15, '#f4ead0'], [1, 15, '#fff8e8'],
    [2, 16, '#fff8e8'], [1, 16, '#d8ccb0'], [3, 16, '#c8b898'], [4, 13, '#c8b898']]) { p.set(i, j, c); m.set(i, j, '#ffffff'); }
  const lettering = (s, x, y, col, sh, shine) => {
    for (const pass of [0, 1]) {
      let cx = x;
      for (const ch of s) {
        FONT3[ch].forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') {
          if (pass === 0) p.set(cx + i + 1, y + j + 1, sh);
          else { p.set(cx + i, y + j, col); if (shine) m.set(cx + i, y + j, '#ffffff'); }
        } });
        cx += 4;
      }
    }
  };
  lettering('KKOKKO', 19, 3, '#ffd84a', '#4a0810', true);
  lettering('CHICKEN', 17, 10, '#fff4e2', '#4a0810', false);
  for (let i = 17; i < 44; i++) if (i % 3) p.set(i, 17, '#ffc23a');
  return { top: p.done(), mask: m.done() };
}
/** static steel: frame box, header, lamp housings, catwalk and the legs down to the tower's roof */
function renderBBFrame() {
  const p = pix(W, H), x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h, deck = y1 + 3;
  for (const lx of [BB.x + 3, BB.x + 16, BB.x + 31, BB.x + BB.w - 4]) {
    for (let y = deck + 1; y < BBT.top; y++) { p.set(lx, y, (y & 1) ? '#3a3462' : '#4a4274'); p.set(lx + 1, y, '#191530'); }
    p.fill(lx - 1, BBT.top - 1, 4, 1, '#2e2a4c');
  }
  for (let i = BB.x + 3; i < BB.x + BB.w - 3; i++) if ((i & 3) === 1) p.set(i, deck + 3, '#2a2548');
  p.fill(x0, y0, BB.w + 2, BB.h + 2, '#1a1428');
  for (let i = x0; i <= x1; i++) { p.set(i, y0, '#3e3458'); p.set(i, y1, '#2c2440'); }
  for (let y = y0; y <= y1; y++) { p.set(x0, y, '#2c2444'); p.set(x1, y, '#110d1e'); }
  for (let i = x0 - 1; i <= x1 + 1; i++) p.set(i, y0 - 1, (i - x0) % 4 === 0 ? '#3a3258' : '#241e3a');
  p.set(x0, y0 - 2, '#2a2442'); p.set(x1, y0 - 2, '#2a2442');
  for (const lx of BB_LAMPS) { p.fill(BB.x + lx - 1, y1 + 1, 3, 1, '#4a4258'); p.fill(BB.x + lx - 1, y1 + 2, 3, 1, '#262238'); }
  for (let i = x0 - 2; i <= x1 + 2; i++) { p.set(i, deck, '#4a4266'); p.set(i, deck + 1, (i & 1) ? '#2a2442' : '#15112a'); }
  return trim(p);
}

/* ================= Seoullo 7017 (the old station overpass, now a lit walkway) ================= */
const SL = { x0: 338, y: 183 };                                // deck top at x0; it rises 1 px every 60 px to the right
const slY = x => SL.y - Math.floor((x - SL.x0) / 60);
const SL_PIERS = [352, 392, 432, 472];
const SL_POTS = [344, 358, 373, 386, 401, 417, 430, 446, 461, 476];
function renderSeoullo() {
  const p = pix(W, H);
  for (const px0 of SL_PIERS) for (let y = slY(px0) + 6; y < 206; y++) {
    p.set(px0, y, '#1c2850'); p.set(px0 + 1, y, '#2a3a6c'); p.set(px0 + 2, y, '#141c3a');
    if (y > 200) p.set(px0 - 1, y, '#141a30');
  }
  for (let x = SL.x0; x < W; x++) {
    const y = slY(x);
    p.set(x, y - 2, x % 4 === 0 ? '#5a6a9a' : '#303c66');                                    // hand rail
    if (x % 4 === 0) p.set(x, y - 1, '#303c66');
    p.set(x, y, '#9aa8d8'); p.set(x, y + 1, '#5c6c9e');                                       // deck edge
    p.set(x, y + 2, '#3a5cd0'); p.set(x, y + 3, '#2440a0');                                    // the blue LED line
    p.set(x, y + 4, '#1a2448'); p.set(x, y + 5, '#10162c');
  }
  for (const x of SL_POTS) {                                                                   // round planters with shrubs
    const y = slY(x);
    p.fill(x - 3, y - 3, 7, 3, '#1c2240'); p.fill(x - 3, y - 1, 7, 1, '#6a8cff');
    for (let i = -3; i <= 3; i++) for (let j = 0; j < 4; j++) if (Math.hypot(i, j * 1.3) < 3.4 && hash(x + i, j, 2) < 0.8)
      p.set(x + i, y - 4 - j, j > 1 ? '#20304a' : '#2a4058');
  }
  return trim(p);
}
const SL_WALKERS = [
  { k: 3, ph: 0.1, dir: 1, coat: '#2a2438' }, { k: 2, ph: 0.45, dir: -1, coat: '#3a2a30' },
  { k: 4, ph: 0.7, dir: 1, coat: '#1e2230' }, { k: 3, ph: 0.9, dir: -1, coat: '#34303e' }, { k: 2, ph: 0.3, dir: 1, coat: '#262a36' },
];

/* ================= Namdaemun Market blocks and the street behind the tent (neon everywhere) ================= */
const BLOCKS = [
  { x: 0, w: 34, top: 160 }, { x: 34, w: 30, top: 167 }, { x: 64, w: 28, top: 158 }, { x: 92, w: 32, top: 169 },
  { x: 124, w: 26, top: 157 }, { x: 150, w: 20, top: 167 }, { x: 170, w: 28, top: 153 }, { x: 198, w: 24, top: 163 },
  { x: 222, w: 22, top: 171 },
];
const SHOP_Y = 192;                                   // shop fronts along the market street (lit)
// sign boxes fit their 6 x 6 glyphs on a 7 px pitch: vertical 8 wide, horizontal 8 tall
const sign = (x, y, v, style, col, g, hl, more = {}) => ({ x, y, v, style, col, g, hl, w: v ? 8 : g.length * 7 + 1, h: v ? g.length * 7 + 1 : 8, ...more });
const MSIGNS = [
  sign(127, 158, true, 'tube', '#ff5ab4', [0, 3, 5, 1], 'pink', { flick: [[37, 38.6], [151, 152.2]] }),
  sign(140, 163, true, 'lit', '#5ee8ff', [2, 6, 4], 'cyan', { ink: '#08304a' }),
  sign(150, 168, false, 'lit', '#ffe060', [7, 1, 3], 'yellow', { ink: '#5a3a00', flick: [[119, 120.4]] }),
  sign(177, 155, true, 'seq', '#c2b8ff', [4, 0, 6, 2], 'lav'),
  sign(202, 164, true, 'lit', '#6af07a', [5, 7, 0], 'green', { ink: '#083a14', flick: [[63, 64.1], [212, 213.5]] }),
  sign(226, 172, true, 'tube', '#ff4a4a', [3, 6], 'red2'),
  sign(150, 180, false, 'tube', '#5ee8ff', [2, 5, 0], 'cyan', { flick: [[176, 177.6]] }),
  sign(18, 160, true, 'lit', '#ffd040', [1, 4], 'yellow', { ink: '#4a2a00' }),
  sign(64, 161, false, 'tube', '#ff5ab4', [6, 2, 7], 'pink', { flick: [[96, 97.3]] }),
  sign(36, 168, false, 'lit', '#8ec8ff', [4, 1, 5], 'cyan', { ink: '#0a2040' }),
];
const ROOF_LETTERS = { x: 174, y: 146, g: [3, 0, 5], col: '#ff6a5a' };   // channel letters on the tallest market block
const CROSSES = [[79, 147], [24, 150]];                                   // red neon church crosses on far roofs
const MARQUEE = { x: 200, y: 188, w: 20, h: 5, len: 60 };                  // a red LED ticker over a shop
function renderSign(s, on, lit = s.g.length) {
  const p = pix(s.w, s.h), c = C(s.col), dim = k => mixRGB([10, 6, 16], c, k);
  for (let y = 0; y < s.h; y++) for (let i = 0; i < s.w; i++) {
    const edge = i === 0 || y === 0 || i === s.w - 1 || y === s.h - 1;
    if (edge) { p.set(i, y, '#120c1a'); continue; }
    if (s.style === 'lit') {
      const v = 1 - Math.abs(i - s.w / 2) / s.w * 0.6 - Math.abs(y - s.h / 2) / s.h * 0.4;
      p.set(i, y, on ? (v - 0.55 > bayer(i, y) * 0.4 ? mixRGB(c, [255, 255, 255], 0.35) : c) : dim(0.22));
    } else p.set(i, y, '#1a1024');
  }
  s.g.forEach((gi, k) => {
    const gx = s.v ? 1 : 1 + k * 7, gy = s.v ? 1 + k * 7 : 1, g = G6[gi], lk = on && k < lit;
    if (s.style === 'lit') { glyphP(p, g, gx, gy, lk ? s.ink : dim(0.1)); return; }
    if (lk) for (let j = -1; j <= 6; j++) for (let i = -1; i <= 6; i++) {                  // tube glow inside the box
      const near = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]].some(([a, b]) => g[j + b] && g[j + b][i + a] === '#');
      if (near && !(g[j] && g[j][i] === '#') && i + gx > 0 && i + gx < s.w - 1 && j + gy > 0 && j + gy < s.h - 1) p.set(gx + i, gy + j, dim(0.35));
    }
    glyphP(p, g, gx, gy, lk ? mixRGB(c, [255, 255, 255], 0.45) : dim(0.2));
  });
  return p.done();
}
function renderMarket() {
  const p = pix(W, H), r = rng(57), wins = [], shops = [];
  for (const b of BLOCKS) {
    const shade = r() < 0.5 ? '#161226' : '#1a152b';
    for (let y = b.top; y < 202; y++) for (let i = b.x; i < b.x + b.w; i++) {
      let col = (y - b.top) % 7 === 6 ? '#13101f' : shade;
      if (y === b.top) col = '#302a4c'; else if (i === b.x) col = '#241e3e'; else if (i === b.x + b.w - 1) col = '#0f0c1c';
      p.set(i, y, col);
    }
    for (let i = b.x + 1; i < b.x + b.w - 1; i += 2) if (r() < 0.6) p.set(i, b.top - 1, '#221c3a');   // rooftop railing
    if (r() < 0.5) { const tx = b.x + 3 + Math.floor(r() * Math.max(1, b.w - 10)); p.fill(tx, b.top - 4, 5, 3, '#221c38'); p.fill(tx, b.top - 4, 5, 1, '#3a3258'); }
    for (let wy = b.top + 3; wy < SHOP_Y - 4; wy += 7) for (let wx = b.x + 2; wx < b.x + b.w - 3; wx += 4) {
      if (r() > 0.6) continue;
      p.fill(wx, wy, 2, 3, '#0d0a18');
      wins.push({ id: wins.length, x: wx, y: wy, n: 3 + Math.floor(r() * 8), p: 0.35 + r() * 0.35, off: r(),
        col: r() < 0.6 ? (r() < 0.5 ? '#ffcf80' : '#ffb45e') : '#b8d0ff' });
    }
    // shop fronts: lit counters with awnings, or a pulled-down shutter
    for (let sx = b.x + 1; sx < b.x + b.w - 3; sx += 10) {
      const sw = Math.min(8, b.x + b.w - 1 - sx), kind = r();
      if (kind < 0.22) { for (let y = SHOP_Y; y < 201; y++) for (let i = sx; i < sx + sw; i++) p.set(i, y, (y & 1) ? '#3a3850' : '#2c2a40'); continue; }
      const warm = kind < 0.7, ramp = warm ? ['#8a4a20', '#c87a34', '#f0aa52', '#ffd488'] : ['#3a5a8a', '#5a86b8', '#8ab8e0', '#d0ecff'];
      for (let y = SHOP_Y + 1; y < 201; y++) for (let i = sx; i < sx + sw; i++) p.set(i, y, pick(ramp, 1 - (y - SHOP_Y) / 9 * 0.7, i, y));
      const aw = ['#c83a3a', '#2a6ac8', '#3aa860', '#e0a020'][Math.floor(r() * 4)];
      for (let i = sx - 1; i <= sx + sw; i++) { p.set(i, SHOP_Y, aw); if ((i - sx) % 2 === 0) p.set(i, SHOP_Y + 1, aw); }
      for (let i = sx + 1; i < sx + sw - 1; i += 2) if (r() < 0.6) p.set(i, 199, ['#e8d0a0', '#5a3a2a', '#c85a3a', '#f0f0e0'][Math.floor(r() * 4)]);
      shops.push({ x: sx + (sw >> 1), warm });
    }
  }
  // sign boxes (off state; the lit faces are drawn per frame) and their brackets
  for (const s of MSIGNS) {
    p.fill(s.x, s.y, s.w, s.h, '#120c1a');
    if (s.v) for (let y = s.y + 3; y < s.y + s.h; y += 8) p.set(s.x - 1, y, '#2a2238');
  }
  // rooftop channel letters on their frame
  const L = ROOF_LETTERS;
  for (let i = L.x - 1; i < L.x + 21; i++) { p.set(i, L.y + 7, '#2a2238'); if ((i - L.x) % 7 === 2) for (let y = L.y + 7; y < 154; y++) p.set(i, y, '#221c30'); }
  // neon crosses (lit per frame), their frames
  for (const [cx, cy] of CROSSES) { for (let y = cy + 10; y < cy + 12; y++) p.set(cx, y, '#221c30'); }
  // LED ticker frame
  p.fill(MARQUEE.x - 1, MARQUEE.y - 1, MARQUEE.w + 2, MARQUEE.h + 2, '#1a0c10');
  return { ...trim(p), wins, shops };
}
function renderCross(on) {                             // 5 x 11 neon cross
  const p = pix(5, 11), c = on ? '#ff3a3a' : '#4a1418', core = on ? '#ffc0b4' : '#5a1c20';
  for (let y = 0; y < 11; y++) p.set(2, y, y === 0 || y === 10 ? c : core);
  for (let i = 0; i < 5; i++) p.set(i, 3, i === 0 || i === 4 ? c : core);
  return p.done();
}
function renderRoofLetters(on) {
  const L = ROOF_LETTERS, p = pix(20, 6), c = C(L.col);
  L.g.forEach((gi, k) => glyphP(p, G6[gi], k * 7, 0, on ? mixRGB(c, [255, 255, 255], 0.25) : mixRGB([20, 8, 12], c, 0.25)));
  return p.done();
}
function renderMarqueeStrip() {                         // the ticker's full text, twice as long as it needs to scroll
  const M = MARQUEE, p = pix(M.len, M.h), seq = [3, 0, 6, 1, 4, 2, 7, 5, 0, 3];
  for (let k = 0; k * 6 < M.len; k++) glyphP(p, G5[seq[k % seq.length]], k * 6 + 1, 0, '#ff5a3a');
  for (let i = 0; i < M.len; i++) for (let j = 0; j < M.h; j++) if (!p.alpha(i, j) && (i + j) % 2 === 0) p.set(i, j, '#3a0c0c');
  return p.done();
}

/* ================= Sungnyemun (Namdaemun): stone base with the arched passage, two-tier hipped roof ================= */
// Front 5 bays, a two-storey pavilion under a hipped roof (a trapezoid from the front), white-plastered ridges,
// dancheong brackets under both eaves, the vertical name plaque, the restored wall stubs east (left) and west.
const STONE = ['#33241a', '#553e2c', '#7c5c3a', '#a8804c', '#cfa462', '#eec47e', '#ffe2a2'];
const G_LAMPS = [-38, -15, 15, 38];                    // floodlamps at the foot of the base (x offsets)
const gEaveU = e => 155 - (e > 18 ? 7 * ((e - 18) / 24) ** 2 : 0);          // upper eave line, e = |x - cx|
const gEaveL = e => 171 - (e > 24 ? 7.5 * ((e - 24) / 24) ** 2 : 0);        // lower eave line
function renderGate() {
  const p = pix(W, H), cx = GATE.cx, g = GATE.g;
  // wall stubs: east (left, receding) and west (right), crenellated parapets
  const wallTop = x => x < cx ? 186 + Math.round((cx - 49 - x) * 0.12) : 187;
  for (let x = 210; x <= 354; x++) {
    if (x > cx - 50 && x < cx + 50) continue;
    const top = wallTop(x), far = x < cx ? (cx - 49 - x) / 34 : 0;
    for (let y = top; y < g; y++) {
      const course = (g - 1 - y) % 3 === 0, joint = ((x + ((g - y) / 3 | 0) * 4) % 7) === 0;
      const v = 0.5 - far * 0.25 + 0.2 * smooth(192, 205, y) + (hash(x >> 2, y / 3 | 0, 6) - 0.5) * 0.2;
      p.set(x, y, course || joint ? '#2a1c14' : pick(STONE, v, x, y));
    }
    const m = (x - 211) % 5;                                                                  // merlons with a gap
    for (let y = top - 3; y < top; y++) if (m !== 0) p.set(x, y, y === top - 3 ? '#e8c07a' : pick(STONE, 0.45 - far * 0.2, x, y));
    if (m === 2) p.set(x, top - 2, '#1a120c');
  }
  // stone base with a slight batter, ashlar courses lit by floodlamps at its foot
  for (let y = 180; y < g; y++) {
    const hw = 44.5 + (y - 180) * 4 / 25, row = (g - 1 - y) / 3 | 0, course = (g - 1 - y) % 3 === 0;
    for (let x = Math.round(cx - hw); x <= Math.round(cx + hw); x++) {
      const bw = 7 + (row % 2), bi = Math.floor((x - cx + 60 + (row & 1) * 3) / bw), joint = (x - cx + 60 + (row & 1) * 3) % bw === 0;
      let v = 0.42 + 0.2 * smooth(184, 205, y) + (hash(bi, row, 7) - 0.5) * 0.22;
      for (const lx of G_LAMPS) v += 0.3 * Math.max(0, 1 - Math.hypot((x - cx - lx) / 11, (g - y) / 24));
      if (Math.abs(x - cx) > hw - 1.2) v -= 0.18;
      p.set(x, y, course || joint ? pick(['#2a1a10', '#4a3220'], v - 0.3, x, y) : pick(STONE, v, x, y));
    }
  }
  for (let x = cx - 45; x <= cx + 45; x++) { p.set(x, 179, '#f2d492'); p.set(x, 180, '#9a7446'); }  // coping
  // the arched passage (hongyemun): dark vault, lit voussoir ring, the far opening glowing
  const ar = 9, ac = cx - 0.5, spring = 195;
  for (let y = spring - ar - 1; y < g; y++) for (let x = cx - 11; x <= cx + 10; x++) {
    const dy = Math.max(0, spring - y), d = Math.hypot(x - ac, dy);
    if (d < ar - 0.4) {
      const farGlow = Math.max(0, 1 - Math.hypot((x - ac) / 5, (y - 201) / 7));
      p.set(x, y, pick(['#0e0808', '#1a0e0a', '#3a2010', '#7a4a20', '#c08040'], 0.12 + farGlow * 0.8 + (y > 202 ? 0.15 : 0), x, y));
    } else if (d < ar + 0.9) p.set(x, y, dy > 0 || Math.abs(x - ac) > ar - 0.5 ? '#ffe6a8' : null);
  }
  // lower storey: red columns, dark bays, green dancheong beam on top
  const colsL = [258, 272, 285, 299, 312, 325];
  for (let y = 171; y < 179; y++) for (let x = 257; x <= 327; x++) {
    let col = pick(['#1a0c0a', '#2a1410', '#4a2414'], 0.3 + smooth(176, 179, y) * 0.5, x, y);
    if (colsL.some(c => x === c || x === c + 1)) col = colsL.some(c => x === c) ? '#e0643a' : '#9a3420';
    if (y <= 172) col = y === 171 ? ((x & 3) === 1 ? '#e04a3a' : '#5ac4a0') : ((x % 6) < 2 ? '#f2e6c8' : '#2a8a6c');
    p.set(x, y, col);
  }
  // a hipped roof: the eave sags in the middle and sweeps up to the corners, the plastered hips curve down from the
  // ridge and turn up again at the tips; M eave line at the centre, U its rise at the corner, rh ridge (or body)
  // half-width, eh half-span to the tip, th the roof's depth over the eave in the middle
  const hipRoof = (M, U, e0, rh, eh, th) => {
    const tops = {};
    for (let x = cx - eh; x <= cx + eh; x++) {
      const e = Math.abs(x - cx), s2 = clamp((e - e0) / (eh - e0), 0, 1), bot = Math.round(M - U * s2 * s2);
      const s = clamp((e - rh) / (eh - rh), 0, 1), top = Math.round(bot - (e <= rh ? th : 2 + (th - 2) * Math.pow(1 - s, 1.5)));
      tops[x] = top;
      for (let y = top; y <= bot; y++) {
        let col = (x & 1) ? '#222436' : '#191b2a';
        if (y >= bot - 3 && (x & 1)) col = '#2c2e44';
        if (y === bot) col = e > eh - 6 ? '#f2deb0' : (x & 1) ? '#d0b080' : '#b09068';
        else if (y === bot - 1) col = '#3c3c52';
        p.set(x, y, col);
      }
      if (e > rh && e < eh - 1) { p.set(x, top, e > eh - 4 ? '#fff4dc' : '#e6ded0'); p.set(x, top + 1, '#8a8490'); }
      if (e < eh - 2) p.set(x, bot + 1, x % 3 === 0 ? '#eee6cc' : e > eh - 8 ? '#1a5a48' : '#2a8a6c');   // painted rafter ends
    }
    return tops;
  };
  hipRoof(171, 7, 24, 27, 50, 7);
  for (let x = cx - 36; x <= cx + 36; x++) { const k = (x - cx + 36) % 5; p.set(x, 173, k === 1 ? '#6ad6b0' : k === 3 ? '#e25a3c' : '#1e6a54'); }
  // upper storey: red columns, gold-lit board walls with small windows, the vertical name plaque
  const colsU = [266, 276, 287, 297, 308, 318];
  for (let y = 157; y < 164; y++) for (let x = 265; x <= 319; x++) {
    let col = pick(['#6a3a1a', '#9a6030', '#c88a40', '#eab866'], 0.5 + (y - 157) / 7 * 0.3 + ((x & 1) ? 0.06 : 0), x, y);
    const bay = colsU.findIndex(c => x < c), mid = bay > 0 ? (colsU[bay - 1] + colsU[bay]) / 2 : 0;
    if (Math.abs(x - mid) < 1.6 && y >= 159 && y <= 161 && bay !== 3) col = '#2a1408';
    if (colsU.some(c => x === c)) col = '#d8583a';
    if (y === 157) col = (x % 4 < 2) ? '#6ad6b0' : '#e25a3c';
    p.set(x, y, col);
  }
  for (let y = 157; y < 164; y++) for (let x = cx - 2; x <= cx + 1; x++)
    p.set(x, y, x === cx - 2 || x === cx + 1 || y === 157 || y === 163 ? '#f0c060' : (y === 159 && x === cx - 1) || (y === 161 && x === cx) ? '#c89a48' : '#1a1410');
  // upper roof, its white ridge with upturned ends, the small guardian figures on the hips
  const upTops = hipRoof(155, 8, 16, 14, 44, 16), RIDGE = upTops[cx];
  for (let x = cx - 15; x <= cx + 15; x++) { p.set(x, RIDGE - 2, '#f6eedc'); p.set(x, RIDGE - 1, '#d6cebe'); p.set(x, RIDGE, '#8a8290'); }
  for (const d of [-1, 1]) {
    const ex = cx + d * 15;
    p.fill(ex - (d < 0 ? 1 : 0), RIDGE - 5, 2, 3, '#2a2432'); p.set(ex + d, RIDGE - 6, '#2a2432'); p.set(ex, RIDGE - 3, '#4a4250');
    for (let k = 0; k < 5; k++) { const x = cx + d * (27 + k * 2), y = upTops[x]; p.set(x, y - 1, '#141220'); if (k < 2) p.set(x, y - 2, '#141220'); }
  }
  for (let x = cx - 26; x <= cx + 26; x++) { const k = (x - cx + 26) % 4; if (!p.alpha(x, 157)) p.set(x, 157, k < 2 ? '#2a8a6c' : '#1a5a48'); }
  // the island: lawn and curb in front, and three small ginkgo trees
  for (let x = 200; x <= 386; x++) {
    for (let y = 205; y < 208; y++) if (!p.alpha(x, y) || y >= g) p.set(x, y, y === 207 ? '#8a8298' : pick(['#1a2410', '#2a3a18', '#4a5a24'], 0.35 + 0.3 * Math.max(0, 1 - Math.abs(x - cx) / 70), x, y));
  }
  const GOLD = ['#3a2808', '#6a4c10', '#a07818', '#d0a424', '#f4cc3c', '#ffe680'];
  for (const [tx, ty, rw, rh] of [[204, 192, 6, 10], [354, 196, 6, 8], [378, 197, 5, 7]]) {
    for (let y = ty + rh - 2; y < g; y++) p.set(tx, y, '#2a2018');
    for (let y = ty - rh; y < ty + rh - 1; y++) for (let x = tx - rw; x <= tx + rw; x++) {
      const dx = (x - tx) / rw, dy = (y - ty) / rh, dd = dx * dx + dy * dy * (dy < 0 ? 1.2 : 1);
      if (dd > 1 || (dd > 0.7 && hash(x, y, 13) < 0.45)) continue;
      p.set(x, y, pick(GOLD, 0.5 - dx * 0.25 - dy * 0.2 + (hash(x, y, 14) - 0.5) * 0.5, x, y));
    }
  }
  return trim(p);
}

/* ================= The road around the gate, the curb and the near sidewalk (dry, leaves in the gutter) ================= */
const LEAF_GOLD = ['#f8d040', '#e8b422', '#ffe070', '#d09a1a', '#c8b830'];
// The zebra crossing over to the market side. Its bars lie along the traffic, so seen across the road they stack into
// the distance: one row thick and close together at the far curb, two rows near. Its sides run back to ZEB.vx on the
// horizon row ZEB.yh, so the far end is narrower and leans towards the middle of the view.
const ZEB = { x0: 128, x1: 188, vx: 175, yh: 164 };   // near end by the curb, and where its sides converge
const ZEB_ROWS = '.#.#.#.#.##.##.##..##..';            // road rows ROAD.y0.. (far to near): '#' a painted bar
const zebX = (x, y) => Math.round(ZEB.vx + (x - ZEB.vx) * (y + 0.5 - ZEB.yh) / (ROAD.y1 - ZEB.yh));
const ZEB_PAINT = ['#74728c', '#9290aa', '#b0aec4', '#cac8d8'];
function renderStreet() {
  const p = pix(W, H), r = rng(66);
  for (let x = 0; x < W; x++) if (x < 200 || x > 386) { p.set(x, 206, '#2a2638'); p.set(x, 207, '#7a7490'); }   // the far curb beside the island
  for (let y = ROAD.y0; y < H; y++) for (let x = 0; x < W; x++) {
    let col;
    if (y < ROAD.y1) col = pick(['#10101c', '#141422', '#181828', '#1d1c2e'], 0.42 + (hash(x, y, 5) - 0.5) * 0.5 - (y - ROAD.y0) * 0.004, x, y);
    else if (y === ROAD.y1) col = '#0c0b16';
    else if (y === ROAD.y1 + 1) col = '#7a7490';                                              // curb top
    else if (y < WALK) col = '#2a2638';
    else {                                                                                    // paving blocks
      const row = (y - WALK) / 3 | 0, bx = (x + (row & 1) * 3) % 6;
      col = (y - WALK) % 3 === 2 || bx === 0 ? '#1d1a29' : (hash((x + (row & 1) * 3) / 6 | 0, row, 8) < 0.5 ? '#2b2739' : '#2f2a3e');
    }
    p.set(x, y, col);
  }
  // lane paint: dashes and the yellow double centre line, stopping short of the crossing
  const clear = (x, y) => x < zebX(ZEB.x0, y) - 4 || x > zebX(ZEB.x1, y) + 4;
  for (let x = 0; x < W; x++) {
    if ((x % 22) < 11) { if (clear(x, 214)) p.set(x, 214, '#5a5a6e'); if (clear(x, 226)) p.set(x, 226, '#6a6a80'); }
    if (clear(x, 219)) { p.set(x, 219, '#8a7a2a'); p.set(x, 221, '#9a8a30'); }
  }
  // the crossing: white bars on the asphalt, brighter near, a little worn; a stop line before it on the far lanes
  for (let y = ROAD.y0; y < ROAD.y1; y++) {
    if (ZEB_ROWS[y - ROAD.y0] !== '#') continue;
    const near = (y - ROAD.y0) / (ROAD.y1 - 1 - ROAD.y0);
    for (let x = zebX(ZEB.x0, y); x <= zebX(ZEB.x1, y); x++)
      p.set(x, y, hash(x, y, 13) < 0.04 ? '#4a4860' : pick(ZEB_PAINT, 0.48 + near * 0.5 + (hash(x >> 1, y, 14) - 0.5) * 0.16, x, y));
  }
  for (let y = ROAD.y0 + 1; y < 219; y++) p.set(zebX(ZEB.x1 + 9, y), y, '#8e8ca4');
  for (let x = ZEB.x0 - 2; x <= ZEB.x1 + 2; x++) for (let y = WALK; y < WALK + 3; y++) p.set(x, y, (x + y) & 1 ? '#8a7a2a' : '#6a5c20');   // tactile strip
  // fallen ginkgo leaves: a carpet under the tree, drifts in the gutter, a few on the road and the walk
  for (let k = 0; k < 520; k++) {
    let x, y;
    if (k < 240) { x = 372 + r() * 112; y = 244 + r() * 26; if (r() < 0.5 && Math.abs(x - TREE.x) > 30 + (y - 244)) continue; }
    else if (k < 380) { x = r() * W; y = ROAD.y1 - 1 + r() * 4; }
    else if (k < 450) { x = 130 + r() * 350; y = WALK + r() * 34; }
    else { x = r() * W; y = ROAD.y0 + 2 + r() * 20; if (r() < 0.6) continue; }
    const c = LEAF_GOLD[Math.floor(r() * LEAF_GOLD.length)];
    p.set(x, y, c); if (r() < 0.4) p.set(x + 1, y, c); if (k < 240 && r() < 0.3) p.set(x, y + 1, LEAF_GOLD[3]);
  }
  return trim(p);
}

/* ================= Traffic: blue and green buses, orange taxis, cars, a delivery scooter ================= */
const CAR_COLS = ['#1a1a24', '#c8c8d2', '#8a8c9c', '#2a3050', '#4a2028', '#e8e4e0', '#1e2a2a'];
function renderCar(body, taxi) {                        // 14 x 7, facing right
  const p = pix(14, 7), b = C(body), hi = mixRGB(b, [255, 255, 255], 0.35), lo = mixRGB(b, [0, 0, 8], 0.45);
  for (let i = 4; i < 10; i++) p.set(i, 1, i === 4 || i === 9 ? lo : hi);
  for (let i = 3; i < 11; i++) p.set(i, 2, i === 3 || i === 10 || i === 7 ? lo : (i < 7 ? '#3a4a66' : '#2a3450'));
  for (let i = 1; i < 14; i++) p.set(i, 3, i === 1 ? lo : hi);
  for (let i = 0; i < 14; i++) { p.set(i, 4, b); p.set(i, 5, lo); }
  for (const w of [2, 10]) { p.set(w, 6, '#08080c'); p.set(w + 1, 6, '#08080c'); p.set(w, 5, '#101018'); p.set(w + 1, 5, '#101018'); }
  p.set(13, 4, '#fff6d8'); p.set(0, 4, '#ff2a2a');                                            // head and tail lamps
  if (taxi) { p.set(6, 0, '#fff2a8'); p.set(7, 0, '#fff2a8'); }
  return p.done();
}
function renderBus(body, stripe) {                       // 38 x 13, facing right
  const p = pix(38, 13), b = C(body), hi = mixRGB(b, [255, 255, 255], 0.3), lo = mixRGB(b, [0, 0, 10], 0.5), r = rng(body.length * 7);
  for (let i = 1; i < 37; i++) p.set(i, 0, '#b8bccc');
  for (let y = 1; y < 11; y++) for (let i = 0; i < 38; i++) p.set(i, y, y === 1 ? hi : y >= 9 ? lo : b);
  for (let i = 2; i < 36; i++) for (let y = 2; y < 6; y++) {                                  // lit windows with passengers
    const pillar = i % 6 === 1;
    p.set(i, y, pillar ? lo : y === 2 ? '#fff6dc' : '#ffe8b4');
  }
  for (let i = 3; i < 34; i += 2) if (r() < 0.5) { p.set(i, 4, '#3a2c34'); p.set(i, 5, '#3a2c34'); p.set(i, 3, r() < 0.5 ? '#2a2028' : '#ffe8b4'); }
  for (let i = 0; i < 38; i++) p.set(i, 7, stripe);
  for (const d of [7, 22]) for (let y = 3; y < 10; y++) { p.set(d, y, '#10141e'); p.set(d + 1, y, y < 6 ? '#c8d0dc' : '#10141e'); }
  for (let i = 31; i < 37; i++) p.set(i, 2, i % 2 ? '#ffb030' : '#ff9a20');                  // LED destination sign
  p.fill(36, 2, 2, 4, '#1a2030');
  for (const w of [4, 29]) { p.fill(w, 10, 4, 3, '#0a0a10'); p.set(w + 1, 11, '#3a3a48'); }
  p.set(37, 8, '#fff6d8'); p.set(37, 9, '#fff6d8'); p.set(0, 8, '#ff2a2a'); p.set(0, 9, '#ff2a2a');
  return p.done();
}
function renderScooter() {                               // 12 x 11, facing right: rider, delivery box, headlamp
  const p = pix(12, 11);
  p.fill(0, 2, 5, 5, '#e84a3a'); p.fill(0, 2, 5, 1, '#ff7a5a'); p.set(2, 4, '#fff0e0');           // the box
  p.fill(4, 3, 3, 4, '#2a2a3a'); p.fill(5, 0, 3, 3, '#f0f0f4'); p.set(7, 1, '#3a4a6a');            // rider + helmet
  p.fill(7, 4, 3, 1, '#2a2a3a'); p.set(9, 5, '#8a8a9a');
  p.fill(2, 7, 8, 2, '#3a3a4a'); p.fill(8, 5, 2, 3, '#c8c8d0');
  for (const w of [2, 9]) { p.fill(w - 1, 9, 3, 2, '#08080c'); p.set(w, 9, '#4a4a58'); }
  p.set(11, 6, '#fff6d8'); p.set(0, 7, '#ff3030');
  return p.done();
}
function renderGlow(len, mirror) {                        // additive head beam + tail glow for a vehicle `len` px long, facing right
  const w = len + 40, h = 11, p = pix(w, h), head = C('#fff0c8'), tail = C('#ff3020');
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const hx = x - (len + 4), dy = y - 6;
    let a = 0, c = head;
    if (hx >= -3 && hx < 26) a = Math.max(0, 1 - Math.abs(dy) / (1.4 + hx * 0.08)) * Math.max(0, 1 - Math.max(0, hx) / 26) * 0.24;
    const tx = x - 4, td = Math.hypot(tx / 3.2, dy / 2);
    if (td < 1 && a < 0.3) { a = (1 - td) * 0.5; c = tail; }
    const q = Math.floor(a * 10 + bayer(x, y)) / 10;
    if (q > 0) p.set(x, y, c, Math.round(q * 255));
  }
  const c = p.done();
  if (!mirror) return c;
  const [m, mx] = makeCanvas(w, h);
  mx.translate(w, 0); mx.scale(-1, 1); mx.drawImage(c, 0, 0);
  return m;
}
const flip = c => { const [m, mx] = makeCanvas(c.width, c.height); mx.translate(c.width, 0); mx.scale(-1, 1); mx.drawImage(c, 0, 0); return m; };
// Lane flows: every slot moves k laps per loop (loop-safe), so vehicles in a lane never catch each other; what a slot
// carries changes each lap (empty, car, taxi). One slot per bus lane is kept for its bus, which runs on set laps.
const SPAN = W + 150;
const LANES = [
  { y: 213, dir: -1, k: 14, slots: 8, seed: 1 },
  { y: 218, dir: -1, k: 12, slots: 7, seed: 2, bus: { slot: 3, laps: [2, 8], kind: 1 } },
  { y: 225, dir: 1, k: 12, slots: 7, seed: 3, bus: { slot: 0, laps: [1, 7], kind: 0 } },
  { y: 230, dir: 1, k: 10, slots: 6, seed: 4, scooter: { slot: 2, laps: [1, 6] } },
];
LANES.forEach(L => { const r = rng(300 + L.seed); L.ph = Array.from({ length: L.slots }, (_, s) => (s + (r() - 0.5) * 0.28) / L.slots); });
const laneX = (L, u) => Math.round(L.dir > 0 ? lerp(-75, W + 75, u) : lerp(W + 75, -75, u));
/** the loop second a bus on lap `lap` of slot s is well in view (clear of the tent on the left), where it sounds its horn */
function busHorn(L, s, lap) {
  const xc = L.dir > 0 ? 170 : W - 90, u = L.dir > 0 ? (xc - 19 + 75) / SPAN : (W + 75 - (xc - 19)) / SPAN;
  const t = ((((lap + u - L.ph[s]) / L.k) * LOOP) % LOOP + LOOP) % LOOP;
  return { t: +t.toFixed(2), x: xc };
}
const BUS_HORNS = LANES.filter(L => L.bus).flatMap(L => L.bus.laps.map(lap => busHorn(L, L.bus.slot, lap)));

/* ================= The pojangmacha: an orange tarp tent glowing from inside, eomuk and tteokbokki ================= */
const TARP = ['#4a140c', '#7a2412', '#b03a16', '#dc5822', '#f87a34', '#ffa452', '#ffc47a'];
const FLAP = { x0: 104, x1: 121, y0: 192, y1: 249 };   // the door flap at the right end (breathes, drawn per frame)
const POT = { x: 20, y: 211, w: 16 };                  // eomuk pot on the counter
const PAN = { x: 40, y: 213, w: 17 };                  // tteokbokki pan
const CUSTOMERS = [{ x: 64, hue: '#2a2230' }, { x: 78, hue: '#3a2c2a' }, { x: 92, hue: '#1e2230' }];
const BULBS = [[38, 195], [80, 195]];
function renderTent() {
  const T = TENT, p = pix(W, H), r = rng(77);
  const glowAt = (x, y) => Math.max(0, 1 - Math.hypot((x - 62) / 70, (y - 212) / 50));
  // roof: the front slope from the ridge to the eave, lit through from inside, pole seams
  for (let y = T.ridge; y <= T.eave; y++) {
    const f = (y - T.ridge) / (T.eave - T.ridge), xa = Math.round(lerp(T.x0 + 8, T.x0 - 3, f)), xb = Math.round(lerp(T.x1 - 8, T.x1 + 3, f));
    for (let x = xa; x <= xb; x++) {
      let col = pick(TARP, 0.35 + glowAt(x, y) * 0.5 + f * 0.12 + (x - xa < 2 || xb - x < 2 ? -0.2 : 0), x, y);
      if (y === T.ridge) col = '#ffb266';
      if ((x - T.x0 - 4) % 19 === 0) col = '#8a2c14';
      if (y === T.eave) col = '#6a200e';
      p.set(x, y, col);
    }
  }
  // walls: valance with invented glyphs, the clear vinyl window, the skirt
  for (let y = T.eave + 1; y < T.base; y++) for (let x = T.x0 - 3; x <= T.x1 + 3; x++) {
    const inWin = y >= 193 && y <= 230 && x >= T.x0 + 3 && x <= T.x1 - 3 && ![44, 45, 82, 83].includes(x);
    if (inWin) continue;
    let col = pick(TARP, 0.3 + glowAt(x, y) * 0.45 - (y > 232 ? (y - 232) * 0.012 : 0) + (x < T.x0 || x > T.x1 ? -0.2 : 0), x, y);
    if (y === 192 || y === 231) col = '#8a2c14';
    if (y === T.base - 1) col = '#3a100a';
    p.set(x, y, col);
  }
  [3, 1, 6, 0, 4, 7, 2].forEach((gi, k) => glyphP(p, G5[gi], 22 + k * 11, 187, '#fff2e0'));
  // the interior seen through the vinyl: glowing back wall, menu cards, shelf with bottles
  for (let y = 193; y <= 230; y++) for (let x = T.x0 + 3; x <= T.x1 - 3; x++) {
    if ([44, 45, 82, 83].includes(x)) continue;
    let col = pick(['#b85a24', '#e07a30', '#f89a44', '#ffb862', '#ffd48a'], 0.9 - (y - 193) / 37 * 0.55 - Math.abs(x - 62) / 140, x, y);
    if (y >= 196 && y <= 199 && x >= 50 && x <= 100 && (x - 50) % 7 < 5) col = y === 196 ? '#fff6e8' : (x + y) % 3 ? '#fff0dc' : '#a8603a';
    if (y === 205 && x > 60 && x < 112) col = '#7a3a18';
    if (y === 204 && x > 61 && x < 111 && r() < 0.5) col = ['#3a9a4a', '#2a7a3a', '#e8e0c0', '#c83a2a'][Math.floor(r() * 4)];
    p.set(x, y, col);
  }
  // counter: steel edge, wood front; the pot, the pan and the skewers go on top per frame
  for (let x = T.x0 + 3; x <= T.x1 - 3; x++) {
    if ([44, 45, 82, 83].includes(x)) continue;
    p.set(x, 216, '#f2eadc'); p.set(x, 217, '#b8a898');
    for (let y = 218; y <= 230; y++) p.set(x, y, pick(['#4a2414', '#6a3418', '#8a4a22'], 0.5 - (y - 218) * 0.03 + ((x & 3) ? 0 : -0.15), x, y));
  }
  for (let x = POT.x; x < POT.x + POT.w; x++) for (let y = POT.y; y < 216; y++)
    p.set(x, y, y === POT.y ? '#fff6ec' : x === POT.x ? '#e8e0d8' : x === POT.x + POT.w - 1 ? '#7a7078' : y === POT.y + 1 ? '#c8a060' : '#b0a8b0');
  for (let x = PAN.x; x < PAN.x + PAN.w; x++) {
    p.set(x, PAN.y + 1, '#2a2226'); p.set(x, PAN.y + 2, '#3a3036');
    p.set(x, PAN.y, x === PAN.x || x === PAN.x + PAN.w - 1 ? '#4a4048' : ((x * 7) % 5 < 2 ? '#fff0e0' : '#e0302a'));
  }
  // green soju bottles and small glasses between the customers
  for (const bx of [71, 86, 103]) { p.fill(bx, 211, 2, 5, '#3aa85a'); p.set(bx, 210, '#6ad88a'); p.set(bx, 212, '#9af0b0'); p.set(bx + 3, 214, '#e8f0f0'); p.set(bx + 3, 215, '#c8d8d8'); }
  // vinyl: seams, faint condensation along the top, two soft reflections
  for (let x = T.x0 + 3; x <= T.x1 - 3; x++) for (let y = 193; y <= 230; y++) {
    if ([44, 45, 82, 83].includes(x)) continue;
    const refl = Math.abs((x - 30) - (y - 193) * 0.9) < 1 && y < 206;
    if (refl && (x + y) % 3 === 0) p.set(x, y, '#ffe6c8');
    else if (y < 195 && hash(x, y, 4) < 0.2) p.set(x, y, '#ffdcb0');
  }
  for (const px0 of [44, 82]) for (let y = 193; y <= 230; y++) { p.set(px0, y, '#c04418'); p.set(px0 + 1, y, '#7a2412'); }
  // the table and stools outside, the cat's spot under it
  for (let x = 128; x <= 147; x++) { p.set(x, 240, '#ff5a4a'); p.set(x, 241, '#c82a2a'); }
  for (const lx of [129, 146]) for (let y = 242; y < 251; y++) p.set(lx, y, '#a02020');
  for (const sx of [121, 151]) { p.fill(sx, 245, 6, 1, '#3a7ae8'); p.fill(sx, 246, 6, 1, '#2a58b0'); for (let y = 247; y < 251; y++) { p.set(sx, y, '#2a58b0'); p.set(sx + 5, y, '#2a58b0'); } }
  p.set(133, 239, '#3aa85a'); p.set(133, 238, '#6ad88a'); p.set(141, 239, '#e8f0f0');
  return trim(p);
}

/* ================= The hanok eave (top left): end tiles, painted rafters, a lantern and a wind bell ================= */
const eaveBot = x => { const u = clamp(x / TIP.x, 0, 1); return 44 - 3.5 * u - 30 * u ** 5; };
const LANTERN = { x: 62, y: 45 };
const BELL = { x: TIP.x - 1, y: TIP.y + 3 };
function renderEave() {
  const p = pix(W, H), rafters = [];
  for (let x = 0; x <= TIP.x + 3; x++) {
    const ye = Math.round(eaveBot(Math.min(x, TIP.x)));
    const warmth = Math.max(0, 1 - Math.hypot((x - 62) / 90, (ye - 60) / 40));
    for (let y = 0; y <= ye + 1; y++) {
      if (x > TIP.x && y > TIP.y + 2) continue;
      const dep = ye - y;
      let col;
      if (dep < 0) col = x % 4 === 1 || x % 4 === 2 ? '#2a2432' : null;                      // round end tiles hang below
      else if (dep === 0) col = warmth > 0.4 ? '#5a3a36' : '#3a3040';
      else if (dep === 1) col = '#15141e';
      else if (dep <= 3) col = x % 4 === 3 ? '#0c0c12' : (x % 4 === 1 && dep === 2 ? '#f2cc50' : '#2a8a6c');   // flying rafter ends
      else if (dep === 4) col = '#0c0e14';
      else if (dep <= 7) {                                                                          // round rafter ends
        const k = (x + 2) % 4, ring = dep === 5 || dep === 7 || k === 0 || k === 2;
        col = k === 3 ? '#0c0e14' : ring ? '#3a86a0' : '#eee4cc';
        if (dep === 6 && k === 1) col = '#e04a3a';
      } else {
        const lean = -0.18 + 0.8 * smooth(55, TIP.x, x), s = x + (ye - 8 - y) * lean;           // rafters fan out at the corner
        const on = ((Math.round(s) % 4) + 4) % 4 < 2, lit = Math.max(0, 1 - (dep - 8) / 26) * (0.5 + warmth);
        col = on ? pick(['#0e1616', '#162422', '#1e3430', '#2e4a40'], lit, x, y) : pick(['#08090e', '#0e1016', '#161820'], lit * 0.8, x, y);
      }
      p.set(x, y, col);
    }
  }
  // the corner rafter's painted end at the tip and the upturned end tile
  p.fill(TIP.x - 2, TIP.y - 1, 4, 3, '#2a8a6c'); p.set(TIP.x - 1, TIP.y, '#f2cc50'); p.set(TIP.x, TIP.y, '#e04a3a');
  p.fill(TIP.x - 1, TIP.y - 3, 5, 2, '#1a1822'); p.set(TIP.x + 3, TIP.y - 4, '#1a1822'); p.set(TIP.x + 2, TIP.y - 4, '#2a2632');
  // the column at the left edge, lit by the tent on its right side, on a stone plinth
  for (let y = 40; y < 262; y++) for (let x = 0; x < 6; x++) {
    let col = x < 2 ? '#4a1810' : x < 4 ? '#7a2a1a' : x === 4 ? '#a83e24' : '#c8563a';
    if (y > 180) col = mixRGB(C(col), [255, 150, 80], x === 5 ? 0.3 * smooth(180, 230, y) : 0);
    if (y >= 256) col = x === 5 ? '#8a8494' : '#4a4658';
    if (y === 255) col = '#2a2430';
    p.set(x, y, col);
  }
  for (let y = 40; y < 48; y++) for (let x = 0; x < 9; x++) p.set(x, y, y === 40 ? '#e8c050' : (x + y) % 3 ? '#2a8a6c' : '#e25a3c');   // painted bracket
  return trim(p);
}
function renderLantern() {                              // cheongsachorong: blue and red silk, a candle inside
  const p = pix(9, 17);
  for (let y = 0; y < 17; y++) for (let i = 0; i < 9; i++) {
    const hw = y < 2 ? 2.5 : y < 14 ? 4 - Math.abs(y - 7.5) * 0.12 : 2.5, dx = Math.abs(i - 4);
    if (dx > hw) continue;
    let col;
    if (y === 0 || y === 13) col = '#e8b040';
    else if (y < 3 || y > 10) col = dx > hw - 1 ? '#1a2a6a' : '#2a44a0';
    else col = pick(['#8a1a18', '#c8302a', '#ff5a3a', '#ff9a5a'], 1 - dx / 4.5 - Math.abs(y - 6.5) * 0.06, i, y);
    if (y >= 14) col = dx < 1 ? '#e02a2a' : null;
    p.set(i, y, col);
  }
  return p.done();
}

/* ================= The street ginkgo (right foreground, golden) and its lamp ================= */
const CROWN = [
  [447, 4, 13, 16], [438, 26, 17, 15], [457, 22, 16, 15], [430, 52, 18, 17], [452, 48, 22, 19], [472, 40, 13, 19],
  [424, 82, 18, 17], [446, 78, 24, 21], [470, 78, 16, 22], [428, 110, 20, 17], [452, 108, 24, 19], [474, 112, 14, 18],
  [436, 136, 18, 14], [460, 138, 20, 14], [418, 128, 12, 10], [412, 98, 10, 12], [484, 66, 10, 22], [486, 140, 10, 12],
];
const GOLD = ['#221606', '#3e2c08', '#6a4c0e', '#9a7214', '#c89c1c', '#ecc22c', '#ffdc5a', '#fff0a0'];
const TREE_BRANCHES = [
  [446, 256, 444, 150, 7, 5], [444, 150, 432, 96, 3.5, 2], [444, 150, 456, 104, 3.2, 2], [432, 96, 438, 40, 2, 1.2],
  [456, 104, 466, 60, 2, 1.2], [444, 128, 424, 118, 2, 1.2], [446, 118, 470, 124, 2, 1.2],
];
const crownV = (x, y) => { let v = -9; for (const [cx, cy, rx, ry] of CROWN) v = Math.max(v, 1 - ((x - cx) / rx) ** 2 - ((y - cy) / ry) ** 2); return v; };
function renderTree() {
  const p = pix(W, H), glints = [], r = rng(313);
  for (const [x0, y0, x1, y1, w0, w1] of TREE_BRANCHES) {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 2);
    for (let s = 0; s <= n; s++) {
      const u = s / n, x = lerp(x0, x1, u), y = lerp(y0, y1, u), rw = lerp(w0, w1, u) / 2;
      for (let j = Math.floor(y - rw); j <= Math.ceil(y + rw); j++) for (let i = Math.floor(x - rw); i <= Math.ceil(x + rw); i++) {
        if (Math.hypot(i - x, j - y) > rw + 0.3) continue;
        const lit = i - x < -rw * 0.3, bark = hash(i, j >> 1, 4);
        p.set(i, j, lit ? (bark < 0.3 ? '#4a4038' : '#5e5244') : (bark < 0.3 ? '#1a1612' : '#26201a'));
      }
    }
  }
  for (let y = 0; y < TREE.crownB + 4; y++) for (let x = 396; x < W; x++) {                   // dark inner mass
    const v = crownV(x, y) + (vnoise(x / 5, y / 5, 8) - 0.5) * 0.6;
    if (v > 0.4 && hash(x, y, 9) < 0.9) p.set(x, y, v > 0.7 ? '#2a1c06' : '#3a2808');
  }
  const balls = [];
  for (let k = 0; k < 9000 && balls.length < 460; k++) {
    const x = 396 + r() * 96, y = -6 + r() * (TREE.crownB + 8), v = crownV(x, y);
    if (v < -0.08 + r() * 0.1) continue;
    balls.push({ x, y, r: 1.5 + r() * 2.3 * (0.55 + 0.45 * clamp(v + 0.4, 0, 1)), tint: (r() - 0.5) * 0.14 });
  }
  balls.sort((a, b) => a.y - b.y);
  for (const b of balls) {
    const lx = LAMP.x - b.x, ly = LAMP.y - b.y, ld = Math.max(1, Math.hypot(lx, ly));
    const lampK = Math.pow(clamp(1 - ld / 96, 0, 1), 1.1);
    const dx0 = lerp(0.35, lx / ld, lampK), dy0 = lerp(-0.9, ly / ld, lampK), dn = Math.hypot(dx0, dy0) || 1;
    const base = 0.2 + lampK * 0.5 + smooth(10, 110, b.y) * 0.16 + b.tint;
    for (let j = Math.floor(b.y - b.r); j <= Math.ceil(b.y + b.r); j++) for (let i = Math.floor(b.x - b.r); i <= Math.ceil(b.x + b.r); i++) {
      const dx = (i - b.x) / b.r, dy = (j - b.y) / b.r, dd = dx * dx + dy * dy;
      if (dd > 1 || (dd > 0.6 && hash(i, j, 77) < 0.4)) continue;
      const nd = (dx * dx0 + dy * dy0) / dn, fan = hash(i, j, 79) < 0.08 ? 0.2 : 0;
      const L = Math.min(0.97, base + nd * 0.3 - dd * 0.06 + (hash(i, j, 78) - 0.5) * 0.12 + fan);
      p.set(i, j, pick(GOLD, L, i, j));
      if (L > 0.72 && hash(i, j, 21) < 0.2) glints.push([i, j]);
    }
  }
  for (let k = 0; k < 240; k++) {                                                              // loose fans around the edge
    const x = 394 + r() * 96, y = -4 + r() * (TREE.crownB + 10), v = crownV(x, y);
    if (v < -0.45 || v > 0.05) continue;
    const ld = Math.hypot(x - LAMP.x, y - LAMP.y);
    p.set(x, y, pick(GOLD, 0.26 + clamp(1 - ld / 96, 0, 1) * 0.46 + smooth(10, 110, y) * 0.12 + r() * 0.15, Math.round(x), Math.round(y)));
  }
  for (let y = 160; y < 256; y++) { p.set(442, y, (y % 7 === 0) ? '#6a5c4c' : '#54483c'); if (y % 5 === 0) p.set(445, y, '#1a1410'); }
  p.fill(436, 254, 20, 3, '#3a3444'); p.fill(436, 254, 20, 1, '#5a5468');                    // tree grate
  return { ...trim(p), glints };
}
function renderLamp() {
  const p = pix(W, H), X = LAMP.post, hy = LAMP.y - 3;
  for (let y = hy - 3; y < 256; y++) { p.set(X, y, '#262634'); p.set(X + 1, y, (y & 7) === 0 ? '#6a6a84' : '#44445c'); }
  p.fill(X - 1, 250, 4, 6, '#2a2a3a'); p.fill(X - 1, 250, 4, 1, '#4a4a60');
  for (let i = LAMP.x + 5; i <= X + 1; i++) p.set(i, hy - 3 + (i > X - 4 ? 0 : 0), '#44445c');
  p.set(X - 3, hy - 2, '#44445c');
  for (let i = LAMP.x - 6; i <= LAMP.x + 6; i++) {
    const e = Math.abs(i - LAMP.x);
    p.set(i, hy - 1, e === 6 ? '#34344a' : '#50506a'); p.set(i, hy, '#262634');
    if (e <= 5) p.set(i, hy + 1, e === 5 ? '#c8c0d0' : '#fffaf2');
  }
  return trim(p);
}
function renderCone() {                                  // very faint beam (clear air), additive
  const top = LAMP.y, bot = 252, p = pix(W, H), c = C('#fff0d8');
  for (let y = top; y < bot; y++) {
    const f = (y - top) / (bot - top), hw = 5 + f * 38;
    for (let x = Math.floor(LAMP.x - hw); x <= Math.ceil(LAMP.x + hw); x++) {
      const e = Math.abs(x - LAMP.x) / hw, a = Math.pow(1 - e, 1.7) * Math.pow(1 - f, 1.2) * 0.12;
      const q = Math.floor(a * 40 + bayer(x, y)) / 40;
      if (q > 0) p.set(x, y, c, Math.round(q * 255));
    }
  }
  return trim(p);
}
function makePool(rx, ry, col, maxA) {                   // flattened dithered ellipse of light on the ground
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = C(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
const drawPool = (pl, x, y, a) => addBlit(pl.c, x - pl.rx, y - pl.ry, a);

/* ================= Falling ginkgo fans (3 depths): flutter, spin like little propellers, flip ================= */
const GK_SHAPES = [null, ['##.##', '#####', '.###.', '..#..'], ['.##.##.', '#######', '#######', '.#####.', '..###..', '...#...', '...#...']];
const GK_COLS = [
  { f: ['#fff08a', '#f8d040', '#c89a1c'], b: ['#fff4b8', '#f0d470', '#d0b048'] },
  { f: ['#ffd65a', '#e8a822', '#a87010'], b: ['#ffe490', '#e8c060', '#c09a3a'] },
  { f: ['#f8f0a0', '#e0d050', '#9a9a1c'], b: ['#fff8c8', '#e8e080', '#b8b050'] },
];
function makeFan(size, ci, back) {
  const g = GK_SHAPES[size], h = g.length, w = g[0].length, p = pix(w, h), pal = back ? GK_COLS[ci].b : GK_COLS[ci].f;
  g.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] === '#') {
    let col = j === 0 ? pal[0] : j >= h - (size === 2 ? 3 : 2) ? pal[2] : pal[1];
    if (size === 2 && j > 0 && j < 4 && (i + j) % 3 === 0 && !back) col = pal[0];               // veins fanning out
    p.set(i, j, col);
  } });
  return p.done();
}
const GK = [[], [], []];
{
  const r = rng(909);
  [
    { count: 30, nMin: 6, nMax: 9, amp: 3, drift: [60, 150], fall: [150, 200], tree: 0.3 },
    { count: 22, nMin: 8, nMax: 12, amp: 5, drift: [120, 260], fall: [90, 150], tree: 0.6 },
    { count: 12, nMin: 10, nMax: 14, amp: 9, drift: [200, 360], fall: [120, 190], tree: 0.75 },
  ].forEach((d, size) => {
    for (let i = 0; i < d.count; i++) GK[size].push({
      id: size * 100 + i, size, n: d.nMin + Math.floor(r() * (d.nMax - d.nMin + 1)), ph: r(),
      ks: 30 + Math.floor(r() * 40), p2: r(), kr: (r() < 0.5 ? -1 : 1) * (40 + Math.floor(r() * 60)), p3: r(), kf: 30 + Math.floor(r() * 50), p4: r(),
      amp: d.amp * (0.7 + r() * 0.6), drift: lerp(d.drift[0], d.drift[1], r()), fall: lerp(d.fall[0], d.fall[1], r()),
      tree: d.tree, col: Math.floor(r() * 3), spin: r() < 0.55,
    });
  });
}
// gusts shake a flurry of fans off the tree (and swing the lantern, the wind bell and the tent flap)
const GUSTS = [[40, 49], [126, 135], [202, 211]];
const gust = t => GUSTS.reduce((a, [g0, g1]) => a + env(t, g0, g1, 2.5), 0);
const FLURRY = [];
{ const r = rng(515); GUSTS.forEach(([g0, g1], gi) => { for (let i = 0; i < 14; i++) FLURRY.push({ id: 700 + gi * 20 + i, t0: g0 + r() * (g1 - g0 - 5), dur: 4 + r() * 3.5, x0: 404 + r() * 76, y0: 10 + r() * 140, size: r() < 0.35 ? 2 : 1, col: Math.floor(r() * 3), k: 3 + r() * 5, sp: (r() < 0.5 ? -1 : 1) * (6 + r() * 8) }); }); }

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Seoul's own falling things are the ginkgo fans (only with its own weather); rain, snow and leaves land on the
// market street, on the road and past the bottom of the frame, and catch the tent, the lamp, the gate and the neon
const WEATHER = weatherPlans(['ginkgo']);
const WX_LAND = [[176, 205], [ROAD.y0 + 2, 250], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: 64, y: 214, rx: 72, ry: 44, col: '#ffb060' }, { x: LAMP.x, y: 180, rx: 34, ry: 64, col: '#fff0d0' },
  { x: GATE.cx, y: 178, rx: 58, ry: 34, col: '#ffd890' }, { x: 66, y: 54, rx: 16, ry: 14, col: '#ff7a5a' },
  { x: 182, y: 176, rx: 52, ry: 22, col: '#ff9ad0' }, { x: TW.c, y: 76, rx: 14, ry: 14, col: '#d0c8ff' },
  { x: 410, y: 186, rx: 70, ry: 7, col: '#8aa8ff' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + BB.h + 1, rx: 30, ry: 12, col: '#ffd8c0' }] };

/* ================= Timed events (loop seconds) ================= */
const PLANE = { t0: 28, t1: 112, x0: W + 12, x1: -14, y0: 60, y1: 48 };
const HELI = { t0: 148, t1: 216, x0: -12, x1: 330, y0: 102, y1: 90 };
const AMBU = { t0: 163, t1: 181 };                     // an ambulance on the road behind the gate (siren)
const CHEERS = [[68, 71.5], [171, 174]];               // the customers raise their glasses
const BELL_T = GUSTS.map(([g0]) => g0 + 3.2);          // the wind bell rings as each gust peaks
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };   // 160 per loop

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR, HILL, TOWER_C, TWRS, BBAD, BB_PITCH, BB_FRAME, SEOULLO_C, SL_GLOW, MARKET, SIGN_C,
  CROSS_C, LETTERS_C, MARQ_C, GATE_C, STREET_C, CARS, GLOWS, BUS_C, SCOOT_C, TENT_C, EAVE_C, LANTERN_C, TREE_C, LAMP_C,
  CONE_C, POOL, TPOOL, GPOOL, FAN_SPR, PUFF_C, VIG_C;
function renderVignette() {
  const p = pix(W, H), c = [6, 4, 18];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function renderSlGlow() {                               // Seoullo's blue light spilling on its planters and piers (additive)
  const p = pix(W, H), c = C('#3a64ff');
  for (let x = SL.x0; x < W; x++) for (let dy = -6; dy <= 8; dy++) {
    const y = slY(x) + 2 + dy, a = Math.max(0, 1 - Math.abs(dy) / (dy < 0 ? 6 : 8)) * 0.3;
    const q = Math.floor(a * 8 + bayer(x, y)) / 8;
    if (q > 0) p.set(x, y, c, Math.round(q * 255));
  }
  return trim(p);
}
function prerender() {
  HL = {
    moon: makeHalo(36, '#8a8cff', 0.18, 1.8), moonCore: makeHalo(12, '#eef0ff', 0.26, 1.5),
    wash: WASH.map(c => makeHalo(40, c, 0.2, 1.6)), washLow: WASH.map(c => makeHalo(22, c, 0.2, 1.4)),
    pod: makeHalo(12, '#fff0d8', 0.3), red: makeHalo(5, '#ff3030', 0.6, 1), strobe: makeHalo(4, '#e8f0ff', 0.6, 1),
    cabin: makeHalo(5, '#ffd8a0', 0.4, 1.2), trail: makeHalo(3, '#ffc070', 0.35, 1),
    pink: makeHalo(18, '#ff4fa8', 0.3), cyan: makeHalo(16, '#40e0ff', 0.28), yellow: makeHalo(16, '#ffd040', 0.26),
    lav: makeHalo(20, '#b0a0ff', 0.32), green: makeHalo(16, '#50f070', 0.26), red2: makeHalo(14, '#ff3a3a', 0.3),
    cross: makeHalo(7, '#ff3030', 0.45), letters: makeHalo(20, '#ff5a4a', 0.24), shop: makeHalo(10, '#ffb060', 0.22),
    band: makeHalo(12, '#8ec8ff', 0.3), bandL: makeHalo(12, '#c2b8ff', 0.3),
    gate: makeHalo(70, '#ffc870', 0.14, 1.6), gateMid: makeHalo(34, '#ffd898', 0.18, 1.5), gLamp: makeHalo(8, '#fff0c8', 0.5),
    eaveGlow: makeHalo(14, '#80ffd0', 0.14),
    bbGlow: makeHalo(34, '#ffb0a0', 0.1), bbLamp: makeHalo(6, '#ffe8c8', 0.5),
    ambR: makeHalo(12, '#ff2a2a', 0.55, 1.2), ambB: makeHalo(12, '#3a6aff', 0.55, 1.2),
    tent: makeHalo(80, '#ff9a4a', 0.16, 1.5), tentCore: makeHalo(30, '#ffc080', 0.2), bulb: makeHalo(12, '#fff0c0', 0.5, 1.3),
    lantern: makeHalo(14, '#ff5a3a', 0.4), lanternBig: makeHalo(34, '#ff7a4a', 0.12),
    lamp: makeHalo(60, '#ffe6c0', 0.16, 1.7), lampCore: makeHalo(14, '#fff8f0', 0.42, 1.4),
    head: makeHalo(4, '#fff4d0', 0.6, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR = renderFarCity(); HILL = renderHill(); TOWER_C = WASH.map(renderTower); TWRS = renderTowers();
  BBAD = { back: [renderBBBack(-1), renderBBBack(1)], ...renderBBTop() };
  BB_PITCH = makePitch(BB.w, BB.h, { accent: '#c2b8ff' }); BB_FRAME = renderBBFrame();
  SEOULLO_C = renderSeoullo(); SL_GLOW = renderSlGlow();
  MARKET = renderMarket();
  SIGN_C = MSIGNS.map(s => s.style === 'seq' ? s.g.map((_, k) => renderSign(s, true, k + 1)).concat([renderSign(s, false)]) : [renderSign(s, false), renderSign(s, true)]);
  CROSS_C = [renderCross(false), renderCross(true)]; LETTERS_C = [renderRoofLetters(false), renderRoofLetters(true)]; MARQ_C = renderMarqueeStrip();
  GATE_C = renderGate(); STREET_C = renderStreet();
  CARS = CAR_COLS.map(c => renderCar(c, false)).concat([renderCar('#e8782a', true), renderCar('#b8b8c4', true)]);
  CARS = CARS.map(c => [flip(c), c]);                                                 // [left-facing, right-facing]
  GLOWS = { car: [renderGlow(14, true), renderGlow(14, false)], bus: [renderGlow(38, true), renderGlow(38, false)], scoot: [renderGlow(12, true), renderGlow(12, false)] };
  BUS_C = [renderBus('#1e58d0', '#e8eef8'), renderBus('#34a046', '#f0f4e8')].map(c => [flip(c), c]);
  SCOOT_C = [flip(renderScooter()), renderScooter()];
  TENT_C = renderTent(); EAVE_C = renderEave(); LANTERN_C = renderLantern();
  TREE_C = renderTree(); LAMP_C = renderLamp(); CONE_C = renderCone();
  POOL = makePool(40, 7, '#ffe6c8', 0.3); TPOOL = makePool(64, 8, '#ff9a4a', 0.3); GPOOL = makePool(52, 3, '#ffc870', 0.3);
  FAN_SPR = [null, 1, 2].map(s => s && [0, 1, 2].map(ci => [makeFan(s, ci, false), makeFan(s, ci, true)]));
  PUFF_C = [];
  for (let pr = 1; pr <= 6; pr++) {
    const s = pr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
      const d = Math.hypot(i - pr, y - pr) / pr;
      if (d < 1 && (1 - d) * 1.7 > bayer(i, y) * 0.9) p.set(i, y, y < pr * 0.8 ? '#f8f0f4' : '#d0c4d4');
    }
    PUFF_C[pr] = p.done();
  }
  VIG_C = renderVignette();
}

/* ================= Per-frame: sky, Namsan, the tower ================= */
function drawScroll(cv, y, k, t) { const off = Math.floor(frac(k * t / LOOP) * W); ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); }
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.fillStyle = s.big ? '#fff8e8' : '#e2e4ff';
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  if (t >= PLANE.t0 && t < PLANE.t1) {                  // a jet on its way to Gimpo: nav lights, strobe
    const s = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(PLANE.x0, PLANE.x1, s)), y = Math.round(lerp(PLANE.y0, PLANE.y1, s));
    rect(ctx, x - 2, y, 6, 1, '#2a2650'); rect(ctx, x, y - 1, 2, 1, '#2a2650'); rect(ctx, x + 3, y - 1, 1, 1, '#2a2650');
    rect(ctx, x - 2, y, 1, 1, '#ff4a4a'); rect(ctx, x + 3, y + 1, 1, 1, '#4aff8a');
    const f = frac(t);
    if (f < 0.08 || (f > 0.18 && f < 0.24)) { rect(ctx, x + 1, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x + 1, y - 1, 0.9); }
  }
  if (t >= HELI.t0 && t < HELI.t1) {                    // a helicopter crossing over Namsan: red beacon, white nose light
    const s = (t - HELI.t0) / (HELI.t1 - HELI.t0), x = Math.round(lerp(HELI.x0, HELI.x1, s)), y = Math.round(lerp(HELI.y0, HELI.y1, s) + wave(t, 40) * 0.8);
    rect(ctx, x - 2, y, 5, 2, '#1e1c34'); rect(ctx, x - 5, y, 3, 1, '#1e1c34'); rect(ctx, x - 3, y - 1, 7, 1, frac(t * 8) < 0.5 ? '#3a3858' : '#24223e');
    rect(ctx, x + 3, y + 1, 1, 1, '#fff6e0');
    if (frac(t / 1.2) < 0.15) { rect(ctx, x, y - 1, 1, 1, '#ff3a3a'); halo(HL.red, x, y - 1, 0.9); }
  }
}
function drawFar(t) {
  blit(FAR);
  for (const w of FAR.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < 0.5) rect(ctx, w.x, w.y, 1, 1, w.col);
}
/** where a cabin of the jig-back cable car is (0 lower station .. 1 upper), 4 round trips per loop */
function cablePos(t, second) {
  const tau = t % 60, s = tau < 6 ? 0 : tau < 30 ? smooth(6, 30, tau) : tau < 36 ? 1 : 1 - smooth(36, 60, tau);
  return second ? 1 - s : s;
}
function drawHill(t) {
  blit(HILL);
  for (const l of TRAIL) { const v = 0.55 + 0.45 * wave(t, 90 + (l.id % 7) * 11, l.id * 0.13); if (v > 0.8) halo(HL.trail, l.x, l.y, v - 0.5); }
  const [[ax, ay], [bx, by]] = CABLE;
  for (const second of [false, true]) {
    const s = cablePos(t, second), x = Math.round(lerp(ax, bx - 2, s)), y = Math.round(lerp(ay, by - 1, s)) + (second ? 2 : 0);
    if (x < 84) continue;                               // hidden behind the market roofs
    rect(ctx, x, y + 1, 1, 1, '#4a4a60');
    rect(ctx, x - 2, y + 2, 5, 3, second ? '#c8402e' : '#e0a83a'); rect(ctx, x - 2, y + 2, 5, 1, second ? '#e86a4a' : '#ffd060');
    rect(ctx, x - 1, y + 3, 3, 1, '#fff0c4');
    halo(HL.cabin, x, y + 3, 0.8);
  }
}
function washAt(t) {                                    // the tower's colour: [current, next, blend]
  const i = step(t, LOOP / WASH_LEN), f = frac(t / WASH_LEN) * WASH_LEN, a = smooth(WASH_LEN - WASH_FADE, WASH_LEN, f);
  return [i % WASH.length, (i + 1) % WASH.length, a];
}
function drawTower(t) {
  const [i, j, a] = washAt(t), T = TOWER_C[i], br = 0.9 + 0.1 * wave(t, 12);
  halo(HL.wash[i], TW.c, 92, (1 - a) * br); halo(HL.wash[j], TW.c, 92, a * br);
  halo(HL.washLow[i], TW.c, 122, (1 - a) * 0.8); halo(HL.washLow[j], TW.c, 122, a * 0.8);
  blit(T);
  if (a > 0) { ctx.globalAlpha = a; blit(TOWER_C[j]); ctx.globalAlpha = 1; }
  halo(HL.pod, TW.c, 74, 0.75);
  // a light chases up the mast every 10 s and flashes at the tip
  const u = frac(t / 10);
  if (u < 0.5) {
    const y = Math.round(lerp(TW.drum - 1, TW.ant, u / 0.5));
    ctx.fillStyle = mix(WASH[i], WASH[j], a);
    ctx.fillRect(TW.c - (y >= 50 ? 2 : 1), y, y >= 50 ? 4 : 2, 2);
    if (u > 0.44) halo(HL.washLow[i], TW.c - 1, TW.ant, (u - 0.44) / 0.06 * 0.8);
  }
  for (let k = 0; k < 3; k++) {                         // visitors strolling round the observation deck
    const v = frac((3 + k) * t / LOOP + k * 0.31), x = TW.c - 7 + Math.floor((1 - Math.abs(2 * v - 1)) * 13);
    rect(ctx, x, k === 2 ? 76 : 72, 1, 2, '#5a4a5a');
  }
}
function drawBlinkers(t, board) {
  const I = blinkI(t);
  if (I <= 0.02) return;
  ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
  const all = [...TWRS.blinks, [TW.c - 1, TW.ant - 1], [TW.c - 3, 48], [TW.c + 2, 48],
    ...(board ? [[BB.x - 1, BB.y - 4], [BB.x + BB.w, BB.y - 4]] : [[BBT.x + 3, BBT.top - 1], [BBT.x + BBT.w - 4, BBT.top - 1]])];
  for (const [x, y] of all) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
}

/* ================= Per-frame: glass towers, the billboard, Seoullo ================= */
function drawTowers(t) {
  blit(TWRS);
  let rx = 0, ry = 0, rw = 0;                           // runs of adjacent lit floors are copied in one go
  for (const s of TWRS.segs) {
    if (hash(s.id + 7, Math.floor(frac(t / LOOP + s.off) * s.n), 4) >= s.p) continue;
    if (rw && s.y === ry && s.x === rx + rw) { rw += s.w; continue; }
    if (rw) ctx.drawImage(TWRS.lit, rx, ry, rw, 1, rx, ry, rw, 1);
    rx = s.x; ry = s.y; rw = s.w;
  }
  if (rw) ctx.drawImage(TWRS.lit, rx, ry, rw, 1, rx, ry, rw, 1);
  TWRS.bands.forEach((b, k) => {                        // LED crowns breathing
    const v = 0.65 + 0.35 * wave(t, 16 + k * 4, k * 0.3);
    ctx.globalAlpha = v; rect(ctx, b.x, b.y, b.w, 1, b.col); ctx.globalAlpha = 1;
    halo(k ? HL.bandL : HL.band, b.x + (b.w >> 1), b.y, v * 0.8);
  });
}
function drawBillboard(t, pitch) {
  const off = inEvent(t, BB_FLICK) && hash(88, Math.floor(t * 20), 3) < 0.55, hum = 0.94 + 0.06 * wave(t, 1700, 0.4);
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + BB.h - 3, (off ? 0.75 : 1) * hum);
  blit(BB_FRAME);
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(BBAD.back[off ? 1 : 0], BB.x, BB.y);
    ctx.drawImage(BBAD.top, BB.x, BB.y);
    ctx.fillStyle = '#fff4e8';                          // steam curling off the drumstick
    for (let k = 0; k < 3; k++) {
      const { cyc, u } = cycle(t, 60 + k * 7, k * 0.33);
      const x = BB.x + 5 + k * 3 + Math.round(wave(t, 90 + k * 13, k * 0.2) * 0.8 + hash(k, cyc, 5)), y = BB.y + 3 - Math.round(u * 4);
      if (y < BB.y) continue;
      ctx.globalAlpha = 0.75 * (1 - u); ctx.fillRect(x, y, 1, 1);
    }
    ctx.globalAlpha = 1;
    const f = frac(t / 12);                             // a gloss sheen rolls over the poster
    if (f < 0.12) {
      const bx = lerp(-12, BB.w + 12, f / 0.12);
      for (let j = 0; j < BB.h; j++) {
        const sx = Math.round(bx - j * 0.6), a0 = Math.max(0, sx), b0 = Math.min(BB.w, sx + 3);
        if (b0 <= a0) continue;
        ctx.globalAlpha = 0.14; rect(ctx, BB.x + a0, BB.y + j, b0 - a0, 1, '#ffffff');
        ctx.globalAlpha = 0.55; ctx.drawImage(BBAD.mask, a0, j, b0 - a0, 1, BB.x + a0, BB.y + j, b0 - a0, 1);
      }
      ctx.globalAlpha = 1;
    }
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    if (off && k === 1) return;
    rect(ctx, BB.x + lx - 1, BB.y + BB.h + 1, 3, 1, '#fff2d6');
    halo(HL.bbLamp, BB.x + lx, BB.y + BB.h + 1, hum);
  });
}
function drawSeoullo(t) {
  addBlit(SL_GLOW.c, SL_GLOW.x, SL_GLOW.y, 0.75 + 0.25 * wave(t, 8));
  blit(SEOULLO_C);
  for (const w of SL_WALKERS) {
    const u = frac(w.k * t / LOOP + w.ph), x = Math.round(w.dir > 0 ? lerp(SL.x0 - 4, W + 4, u) : lerp(W + 4, SL.x0 - 4, u)), y = slY(x);
    const st = Math.floor(t * 3 + w.ph * 9) & 1;
    rect(ctx, x, y - 6, 2, 1, '#14121c'); rect(ctx, x, y - 5, 2, 3, w.coat); rect(ctx, x + (st ? 0 : 1), y - 2, 1, 2, '#0e0c14');
  }
}

/* ================= Per-frame: the market street ================= */
function signLevel(t, ev, salt) {
  if (!ev || !inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 24), 3);
  return hv > 0.55 ? 1 : hv > 0.3 ? 0.4 : 0;
}
const FAR_WALK = [{ k: 3, ph: 0.2, dir: 1, c: '#2a2438' }, { k: 2, ph: 0.55, dir: -1, c: '#3a2a2a' }, { k: 4, ph: 0.8, dir: 1, c: '#1e2436' }, { k: 3, ph: 0.4, dir: -1, c: '#e0dcd4' }];
function drawMarket(t) {
  blit(MARKET);
  for (const w of MARKET.wins) if (hash(w.id + 90, Math.floor(frac(t / LOOP + w.off) * w.n), 5) < w.p) rect(ctx, w.x, w.y, 2, 3, w.col);
  const hum = 0.92 + 0.08 * wave(t, 2400);
  MSIGNS.forEach((s, k) => {
    const S = SIGN_C[k];
    let spr = S[1], lv = signLevel(t, s.flick, 30 + k);
    if (s.style === 'seq') {                            // glyphs light one by one, hold, blink twice, go dark
      const q = frac(t / 8) * 8, n = s.g.length;
      const lit = q < n * 0.6 ? Math.floor(q / 0.6) + 1 : q < 6.4 ? n : q < 7.4 ? (Math.floor(q * 4) & 1 ? n : 0) : 0;
      spr = lit ? S[lit - 1] : S[n]; lv = lit ? 1 : 0.15;
    } else if (lv < 1) spr = lv > 0 ? S[1] : S[0];
    if (lv > 0 && lv < 1) { ctx.drawImage(S[0], s.x, s.y); ctx.globalAlpha = lv; ctx.drawImage(S[1], s.x, s.y); ctx.globalAlpha = 1; }
    else ctx.drawImage(spr, s.x, s.y);
    halo(HL[s.hl], s.x + (s.w >> 1), s.y + (s.h >> 1), lv * hum);
  });
  for (const [cx, cy] of CROSSES) { ctx.drawImage(CROSS_C[1], cx - 2, cy); halo(HL.cross, cx, cy + 4, 0.9); }
  const L = ROOF_LETTERS, lon = !(frac(t / 30) > 0.95 && hash(61, Math.floor(t * 12), 1) < 0.5);
  ctx.drawImage(LETTERS_C[lon ? 1 : 0], L.x, L.y);
  if (lon) halo(HL.letters, L.x + 10, L.y + 3, 0.85);
  const M = MARQUEE, off = Math.floor(frac(12 * t / LOOP) * M.len);   // the ticker scrolls its strip 12 times a loop
  const a = Math.min(M.w, M.len - off);
  ctx.drawImage(MARQ_C, off, 0, a, M.h, M.x, M.y, a, M.h);
  if (a < M.w) ctx.drawImage(MARQ_C, 0, 0, M.w - a, M.h, M.x + a, M.y, M.w - a, M.h);
  for (const s of MARKET.shops) if (s.x > 124) halo(HL.shop, s.x, 197, s.warm ? 0.7 : 0.4);
  for (const f of FAR_WALK) {                           // shoppers on the market sidewalk
    const u = frac(f.k * t / LOOP + f.ph), x = Math.round(f.dir > 0 ? lerp(-6, W + 6, u) : lerp(W + 6, -6, u));
    rect(ctx, x, 196, 1, 1, '#1a1620'); rect(ctx, x, 197, 2, 3, f.c); rect(ctx, x + ((Math.floor(t * 3 + f.ph * 7) & 1) ? 0 : 1), 200, 1, 1, '#0e0c14');
  }
}

/* ================= Per-frame: the road behind the island, the gate, the road in front ================= */
const FAR_CARS = [];
{ const r = rng(404); for (let i = 0; i < 14; i++) FAR_CARS.push({ id: i, dir: i % 2 ? 1 : -1, k: i % 2 ? 10 : 8, ph: (i >> 1) / 7 + r() * 0.05 }); }
function drawFarRoad(t) {
  for (const c of FAR_CARS) {
    const { cyc, u } = cycle(t, c.k, c.ph);
    if (hash(c.id, cyc, 2) < 0.3) continue;
    const x = Math.round(c.dir > 0 ? lerp(-10, W + 10, u) : lerp(W + 10, -10, u)), y = c.dir > 0 ? 204 : 203;
    rect(ctx, x, y - 1, 5, 2, hash(c.id, cyc, 3) < 0.4 ? '#8a8a98' : '#2a2a36');
    rect(ctx, c.dir > 0 ? x + 5 : x - 1, y, 1, 1, '#fff4d0'); rect(ctx, c.dir > 0 ? x - 1 : x + 5, y, 1, 1, '#ff3a2a');
  }
  if (t >= AMBU.t0 && t < AMBU.t1) {                    // the ambulance, lights flashing red and blue
    const x = Math.round(lerp(-12, W + 12, (t - AMBU.t0) / (AMBU.t1 - AMBU.t0))), f = frac(t * 4) < 0.5;
    rect(ctx, x, 201, 8, 3, '#e8e8f0'); rect(ctx, x, 203, 8, 1, '#c83030'); rect(ctx, x + 8, 203, 1, 1, '#fff4d0');
    rect(ctx, x + 2, 200, 2, 1, f ? '#ff3a3a' : '#5a1a1a'); rect(ctx, x + 4, 200, 2, 1, f ? '#1a2a5a' : '#4a7aff');
    halo(f ? HL.ambR : HL.ambB, x + (f ? 3 : 5), 200, 0.9);
  }
}
function drawGate(t) {
  const br = 0.92 + 0.05 * wave(t, 6) + 0.03 * wave(t, 2300);
  halo(HL.gate, GATE.cx, 170, br); halo(HL.gateMid, GATE.cx, 186, br);
  blit(GATE_C);
  for (const lx of G_LAMPS) { rect(ctx, GATE.cx + lx - 1, GATE.g - 1, 3, 1, '#fff6dc'); halo(HL.gLamp, GATE.cx + lx, GATE.g - 1, br); }
  halo(HL.eaveGlow, GATE.cx - 22, 172, br); halo(HL.eaveGlow, GATE.cx + 22, 172, br); halo(HL.eaveGlow, GATE.cx, 157, br * 0.8);
}
function drawStreet(t) {
  blit(STREET_C);
  drawPool(GPOOL, GATE.cx, 210, 0.8);
  drawPool(POOL, LAMP.x + 2, 244, 0.95 + 0.05 * wave(t, 1500, 0.3));
}
function drawTraffic(t) {
  for (const L of LANES) for (let s = 0; s < L.slots; s++) {
    const { cyc, u } = cycle(t, L.k, L.ph[s]), x = laneX(L, u), d = L.dir > 0 ? 1 : 0;
    if (x < -50 || x > W + 10) continue;
    const bus = L.bus && L.bus.slot === s, sc = L.scooter && L.scooter.slot === s;
    if (bus && !L.bus.laps.includes(cyc)) continue;
    if (bus) {
      ctx.drawImage(BUS_C[L.bus.kind][d], x, L.y - 12);
      addBlit(GLOWS.bus[d], d ? x - 4 : x - 36, L.y - 9, 0.9);
      continue;
    }
    if (sc && L.scooter.laps.includes(cyc)) {           // the delivery scooter leans into the lane
      const bob = Math.round(wave(t, 300) * 0.6);
      ctx.drawImage(SCOOT_C[d], x, L.y - 10 + bob);
      addBlit(GLOWS.scoot[d], d ? x - 4 : x - 38, L.y - 8, 0.9);
      continue;
    }
    const hv = hash(L.seed * 97 + s, cyc, 7);
    if (hv < 0.22 || sc) continue;
    const ci = hv > 0.8 ? 7 + (hv > 0.9 ? 1 : 0) : Math.floor(hash(L.seed * 97 + s, cyc, 8) * CAR_COLS.length);
    ctx.drawImage(CARS[ci][d], x, L.y - 6);
    addBlit(GLOWS.car[d], d ? x - 4 : x - 36, L.y - 9, 0.85);
  }
}

/* ================= Per-frame: people on the sidewalk with their shopping bags ================= */
const PEOPLE = [
  { k: 9, ph: 0.02, dir: 1, coat: '#1a1a24', coatD: '#0e0e16', bag: '#e8d8b0', bagD: '#a8946a', hair: '#141014', long: true },
  { k: 10, ph: 0.47, dir: -1, coat: '#e6e2da', coatD: '#a8a49e', bag: '#d0343e', bagD: '#8a1a22', hair: '#2a1a14', long: true },
  { k: 8, ph: 0.9, dir: -1, coat: '#8a6a4a', coatD: '#5a4430', bag: '#1e2230', bagD: '#101218', hair: '#141014', hat: '#d8c8b0' },
  { k: 11, ph: 0.3, dir: 1, coat: '#2a3a5a', coatD: '#18223a', phone: true, hair: '#1a1210', legs: '#3a3a4a' },
  { k: 9, ph: 0.62, dir: 1, coat: '#5a5a68', coatD: '#3a3a44', bag: '#f0c040', bagD: '#a88420', hair: '#3a2a1a', legs: '#20202c', hat: '#c83a4a' },
  { k: 10, ph: 0.12, dir: -1, coat: '#3a2a3a', coatD: '#221822', bag: '#f4f0e8', bagD: '#b8b2a8', hair: '#141014', scarf: '#e84a6a', long: true },
];
const WALK_Y = 258;                                   // feet line, in front of the tent
function drawPerson(P, x, t) {
  const fy = WALK_Y, d = P.dir, R = (i, j, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(d > 0 ? x + i : x - i - w + 1, fy + j, w, h); };
  const f = Math.floor(t * 6 + P.ph * 13) % 4, stride = f === 1 ? 1 : f === 3 ? -1 : 0, bob = f % 2 ? 0 : -1;
  const legs = P.legs || '#16121c';
  R(-1 - stride, -8, 2, 7, legs); R(1 + stride, -8, 2, 7, mix(legs, '#000000', 0.3));        // legs, shoes
  R(-2 - stride, -1, 3, 1, '#0c0a10'); R(1 + stride, -1, 3, 1, '#0c0a10');
  const top = -21 + bob, len = P.long ? 15 : 11;
  R(-2, top, 6, len, P.coat); R(-2, top, 1, len, P.coatD); R(3, top + 1, 1, len - 1, P.coatD);  // padded coat
  for (let j = top + 3; j < top + len - 1; j += 3) R(-1, j, 4, 1, P.coatD);
  if (P.scarf) R(-2, top, 6, 2, P.scarf);
  R(stride > 0 ? -3 : 4, top + 2, 1, 7, P.coatD);                                              // swinging arm
  R(-1, top - 5, 4, 5, P.hair); R(1, top - 3, 2, 3, '#e8b890'); R(2, top - 1, 1, 1, '#c89070');   // head
  if (P.hat) { R(-1, top - 6, 4, 2, P.hat); R(0, top - 7, 2, 1, P.hat); }
  if (P.bag) {                                                                                   // a shopping bag swings at the hand
    const by = top + 9 + (stride ? 0 : 1);
    R(4, by, 1, 2, P.bagD); R(5, by - 1, 2, 1, P.bagD); R(3, by + 2, 5, 6, P.bag); R(7, by + 2, 1, 6, P.bagD); R(3, by + 2, 5, 1, P.bagD);
  }
  if (P.phone) { R(3, top - 2, 1, 2, '#e8f0ff'); halo(HL.head, d > 0 ? x + 3 : x - 3, fy + top - 1, 0.7); }
}
function drawWalkers(t) {
  for (const P of PEOPLE) {
    const u = frac(P.k * t / LOOP + P.ph), x = Math.round(P.dir > 0 ? lerp(-16, W + 16, u) : lerp(W + 16, -16, u));
    if (x < -10 || x > W + 10) continue;
    drawPerson(P, x, t);
  }
}

/* ================= Per-frame: the tent, its cook and customers, steam, the flap, the cat ================= */
let FLAP_C;
function renderFlap() {
  const w = FLAP.x1 - FLAP.x0 + 1, h = FLAP.y1 - FLAP.y0 + 1, p = pix(w, h);
  const FLAPC = ['#3a0e0a', '#5e1810', '#8a2614', '#b8381a', '#d84e22', '#ee6a2c'];   // a heavier, deeper red tarp
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const fold = (i % 5 === 1 ? 0.14 : i % 5 === 3 ? -0.12 : 0) * smooth(4, 20, y);           // folds deepen towards the hem
    let col = pick(FLAPC, 0.62 - y / h * 0.3 + fold - (i > w - 4 ? 0.15 : 0), i, y);
    if (i === 0) col = '#f08a3a';
    if (y === h - 1) col = '#2a0a08';
    if (y > 2 && y < 5 && i > 3 && i < w - 3 && (i & 1)) col = '#fff0dc';                      // stitched glyph band
    p.set(i, y, col);
  }
  return p.done();
}
function drawTent(t) {
  const g = gust(t);
  halo(HL.tent, 64, 214, 0.9); drawPool(TPOOL, 70, 257, 0.9);
  blit(TENT_C);
  ctx.fillStyle = '#e8cc90';                            // fish-cake skewers standing in the broth
  for (let k = 0; k < 6; k++) ctx.fillRect(POT.x + 2 + k * 2 + (k > 2 ? 1 : 0), 206 + (k & 1), 1, 5);
  const cx = 27 + Math.round(wave(t, 12) * 3 + wave(t, 31, 0.3) * 1.2);   // the cook moves between pot and pan
  rect(ctx, cx - 1, 199, 5, 2, '#1a1010'); rect(ctx, cx, 201, 3, 2, '#e8b890'); rect(ctx, cx - 1, 203, 5, 8, '#f0e8e0');
  rect(ctx, cx, 204, 3, 7, '#c83a3a');
  const ladle = Math.round(1 + wave(t, 48, 0.2) * 1.5);
  rect(ctx, cx + 4, 203 + ladle, 1, 4, '#e8e0d8'); rect(ctx, cx + 4, 207 + ladle, 2, 1, '#b8b0b8');
  const cheers = inEvent(t, CHEERS);
  CUSTOMERS.forEach((c, k) => {
    const bob = Math.round(wave(t, 18 + k * 5, k * 0.3) * 0.6);
    const y = 203 + bob;                                // backs to us: hair, rounded shoulders, a quilted puffer
    rect(ctx, c.x - 1, y, 2, 1, '#1a1216'); rect(ctx, c.x - 2, y + 1, 4, 3, '#140e12'); rect(ctx, c.x - 1, y, 2, 1, '#6a4030');
    rect(ctx, c.x - 2, y + 4, 5, 1, c.hue); rect(ctx, c.x - 3, y + 5, 7, 8, c.hue);
    rect(ctx, c.x - 2, y + 4, 4, 1, '#8a5a40'); rect(ctx, c.x - 3, y + 5, 1, 1, '#6a4a3a'); rect(ctx, c.x + 3, y + 5, 1, 1, '#6a4a3a');
    ctx.globalAlpha = 0.35; for (let q = y + 7; q < y + 13; q += 2) rect(ctx, c.x - 3, q, 7, 1, '#000000'); ctx.globalAlpha = 1;
    rect(ctx, c.x - 2, 224, 5, 1, k === 1 ? '#3a7ae8' : '#e84a3a'); rect(ctx, c.x - 2, 225, 1, 5, '#6a2a2a'); rect(ctx, c.x + 2, 225, 1, 5, '#6a2a2a');
    if (cheers && k !== 1) { rect(ctx, c.x + (k ? -3 : 3), 202 + bob, 1, 5, c.hue); rect(ctx, c.x + (k ? -3 : 3), 201 + bob, 1, 1, '#e8f4f4'); }
  });
  for (let k = 0; k < 7; k++) {                         // steam from the pot, inside the vinyl
    const { u } = cycle(t, 50 + k * 6, k * 0.19);
    const x = POT.x + 3 + k * 1.7 + wave(t, 26 + k * 3, k * 0.1) * 1.5 * u, y = 210 - u * 16, rr = 1 + Math.floor(u * 3);
    ctx.globalAlpha = 0.38 * Math.sin(Math.PI * u);
    ctx.drawImage(PUFF_C[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
  BULBS.forEach(([bx, by], k) => {
    const fl = 0.9 + 0.06 * wave(t, 1300 + k * 70, k * 0.4) + 0.04 * wave(t, 3100, k * 0.2);
    rect(ctx, bx, by, 2, 2, '#fffae0'); halo(HL.bulb, bx + 1, by + 1, fl); halo(HL.tentCore, bx + 1, by + 8, fl * 0.6);
  });
  // the door flap breathes in the wind (a gust swings it out)
  const amp = 1.1 + 0.9 * wave(t, 20, 0.1) + 0.5 * wave(t, 47, 0.4) + 3.2 * g, h = FLAP.y1 - FLAP.y0 + 1;
  ctx.fillStyle = '#ffd08a';
  for (let j = 0; j < h; j++) {
    const off = Math.round(Math.max(0, amp) * ((j / h) ** 1.6));
    if (off > 0) ctx.fillRect(FLAP.x0, FLAP.y0 + j, off, 1);                                    // light leaking past the flap
    ctx.drawImage(FLAP_C, 0, j, FLAP_C.width, 1, FLAP.x0 + off, FLAP.y0 + j, FLAP_C.width, 1);
  }
  for (let k = 0; k < 14; k++) {                        // steam escaping from the roof vent, pushed by the wind
    const { u } = cycle(t, 40 + k * 3, k * 0.071);
    const x = 30 + wave(t, 14 + k * 2, k * 0.2) * 2 * u - u * (8 + g * 22), y = TENT.ridge - u * 62 - u * u * 6, rr = Math.min(6, 1 + Math.floor(u * 6));
    ctx.globalAlpha = 0.42 * Math.min(1, u * 8) * Math.pow(1 - u, 1.4);
    ctx.drawImage(PUFF_C[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
  // a ginger cat loafing under the outside table: tail swishes, eyes catch the light and blink
  const CAT = ['..#.#......', '.#####.....', '.#####.....', '##########.', '###########'], X = 131, Y = 246;
  ctx.fillStyle = '#c8783a';
  CAT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') ctx.fillRect(X + i, Y + j, 1, 1); });
  rect(ctx, X + 5, Y + 3, 1, 2, '#8a4a1e'); rect(ctx, X + 7, Y + 3, 1, 2, '#8a4a1e'); rect(ctx, X + 1, Y + 1, 5, 1, '#e0a060');
  const ts = wave(t, 30, 0.2) * 0.9 + wave(t, 97, 0.5) * 0.4;
  for (let j = 0; j < 4; j++) rect(ctx, X + 11 + Math.round(ts * (j / 3) * 1.5), Y + 3 - j, 1, 1, '#c8783a');
  if (!(frac(t / 5) > 0.955 || frac(t / 37) > 0.985)) { rect(ctx, X + 2, Y + 2, 1, 1, '#c8f060'); rect(ctx, X + 4, Y + 2, 1, 1, '#c8f060'); }
  else rect(ctx, X + 2, Y + 2, 3, 1, '#8a4a1e');
}

/* ================= Per-frame: the eave, its lantern and wind bell; the ginkgo and its lamp ================= */
function drawEave(t) {
  const g = gust(t);
  blit(EAVE_C);
  const spr = LANTERN_C, w = spr.width, h = spr.height, sw = wave(t, 30, 0.1) * 0.9 + wave(t, 71, 0.3) * 0.3 + g * 2.2 * wave(t, 97);
  rect(ctx, LANTERN.x, LANTERN.y - 2, 1, 3, '#2a2020');
  for (let j = 0; j < h; j++) ctx.drawImage(spr, 0, j, w, 1, LANTERN.x - (w >> 1) + Math.round(sw * (j + 1) / h), LANTERN.y + 1 + j, w, 1);
  const fl = 0.86 + 0.08 * wave(t, 1211) + 0.06 * wave(t, 2333, 0.7);
  halo(HL.lantern, LANTERN.x + Math.round(sw * 0.5), LANTERN.y + 8, fl); halo(HL.lanternBig, LANTERN.x, LANTERN.y + 8, fl);
  const a = (0.12 + 0.55 * g) * Math.sin(TAU * 110 * t / LOOP), bx = Math.round(BELL.x + Math.sin(a) * 4);
  for (let j = 0; j < 4; j++) rect(ctx, Math.round(BELL.x + Math.sin(a) * j), BELL.y + j, 1, 1, '#3a3028');
  rect(ctx, bx - 1, BELL.y + 4, 3, 3, '#8a6a3a'); rect(ctx, bx - 1, BELL.y + 4, 1, 3, '#c89a5a'); rect(ctx, bx - 2, BELL.y + 7, 5, 1, '#6a502a');
  const fx = Math.round(BELL.x + Math.sin(a * 1.6) * 9);
  rect(ctx, fx, BELL.y + 8, 1, 2, '#3a3028'); rect(ctx, fx - 2, BELL.y + 10, 4, 2, '#b8985a'); rect(ctx, fx + 2, BELL.y + 10, 1, 1, '#b8985a'); rect(ctx, fx + 2, BELL.y + 12, 1, 1, '#8a6a3a');
}
function drawTree(t) {
  blit(TREE_C);
  const gs = step(t, 1440);
  ctx.fillStyle = '#fff6c0';
  TREE_C.glints.forEach(([x, y], i) => { if (hash(i, gs, 31) < 0.03) ctx.fillRect(x, y, 1, 1); });
  const li = 0.96 + 0.04 * wave(t, 1500, 0.3);
  addBlit(CONE_C.c, CONE_C.x, CONE_C.y, 0.7 * li);
  blit(LAMP_C);
  halo(HL.lamp, LAMP.x, LAMP.y, li); halo(HL.lampCore, LAMP.x, LAMP.y - 1, li);
}

/* ================= Per-frame: the ginkgo fans ================= */
function drawFans(list, t) {
  for (const pt of list) {
    const q = t * pt.n / LOOP + pt.ph, fq = Math.floor(q), cyc = fq % pt.n, u = q - fq;
    let x0, y0, fall = pt.fall;
    if (hash(pt.id, cyc, 3) < pt.tree) { x0 = 404 + hash(pt.id, cyc, 1) * 76; y0 = 16 + hash(pt.id, cyc, 2) * 130; }
    else { x0 = 30 + hash(pt.id, cyc, 1) * (W + 60); y0 = -6 - hash(pt.id, cyc, 2) * 16; if (pt.size > 0) fall += 100; }
    const sw = wave(t, pt.ks, pt.p2);
    const x = x0 - u * pt.drift + sw * pt.amp, y = y0 + u * fall + (1 - Math.abs(sw)) * (pt.size + 1) * 0.8;
    if (x < -8 || x > W + 8 || y > 268) continue;
    const a = Math.min(1, u / 0.05, (1 - u) / 0.1);
    const fl = Math.cos(TAU * (pt.kf * t / LOOP + pt.p4)), back = fl < 0;
    if (pt.size === 0 || a < 0.7) {
      ctx.globalAlpha = pt.size ? Math.min(1, a * 1.4) : a * 0.9; ctx.fillStyle = back ? '#fff0a0' : GK_COLS[pt.col].f[1];
      ctx.fillRect(Math.round(x), Math.round(y), Math.abs(fl) > 0.45 ? 2 : 1, 1);
      continue;
    }
    drawFan(pt.size, pt.col, back, x, y, pt.spin ? TAU * pt.kr * t / LOOP : sw * 0.9 + 0.5 * wave(t, Math.abs(pt.kr) >> 1, pt.p3), fl, a);
  }
  ctx.globalAlpha = 1;
}
function drawFan(size, ci, back, x, y, rot, fl, a) {
  const spr = FAN_SPR[size][ci][back ? 1 : 0], w = spr.width, h = spr.height;
  ctx.save();
  ctx.globalAlpha = a;
  ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(rot); ctx.scale(1, Math.max(0.25, Math.abs(fl)));
  ctx.drawImage(spr, -(w >> 1), -(h >> 1));
  ctx.restore();
}
function drawFlurry(t, size) {                          // a gust tears a handful of fans off the tree
  for (const f of FLURRY) {
    if (f.size !== size || t < f.t0 || t >= f.t0 + f.dur) continue;
    const s = (t - f.t0) / f.dur, a = Math.min(1, s / 0.1, (1 - s) / 0.15);
    const x = f.x0 - s * (f.size === 2 ? 300 : 220) + Math.sin(TAU * f.k * s) * 4, y = f.y0 + s * 70 + Math.sin(TAU * f.k * s * 0.5) * 6;
    if (x < -8) continue;
    drawFan(f.size, f.col, Math.cos(TAU * f.k * s) < 0, x, y, f.sp * s, Math.cos(TAU * f.k * s), a);
  }
  ctx.globalAlpha = 1;
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own billboard ad; { pitch } paints a site face on it; { billboard: false } leaves the
 *  billboard out (the loop videos); { weather } draws a WEATHERS pick (site/js/engine/weather.js), the city's own when left out */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawFar(t);
  drawHill(t);
  if (wx.ginkgo) drawFans(GK[0], t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawTower(t);
  drawTowers(t);
  if (billboard) drawBillboard(t, pitch);
  drawBlinkers(t, billboard);
  drawSeoullo(t);
  drawMarket(t);
  drawFarRoad(t);
  drawGate(t);
  drawStreet(t);
  drawTraffic(t);
  if (wx.ginkgo) { drawFans(GK[1], t); drawFlurry(t, 1); }
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawTent(t);
  drawTree(t);
  drawWalkers(t);
  drawEave(t);
  if (wx.ginkgo) { drawFans(GK[2], t); drawFlurry(t, 2); }
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'seoul',
  name: 'Seoul',
  tagline: 'Ginkgo night under Namsan',
  timeZone: 'Asia/Seoul',
  accent: '#c2b8ff',
  focusX: TW.c,
  // Namsan's summit (the cable car's upper station, the plaza, the tower) to the tips of Sungnyemun's lower eaves: an
  // upright phone zooms out to show the tower, the billboard and the whole gate rather than fill with the tower alone
  landmark: [CABLE[1][0] - 8, GATE.cx + 50],
  landmarkTop: TW.ant - 1,                              // the row of the mast's tip light, which landscape crops keep
  billboard: BB,
  ambience: { traffic: 0.45, crowd: 0.25, wind: 0.15 },
  events: [
    ...BUS_HORNS.map(h => ({ t: h.t, type: 'horn', x: h.x, gain: 0.45 })),
    ...BELL_T.map(t => ({ t, type: 'chime', x: BELL.x, gain: 0.6 })),
    { t: AMBU.t0 + 1, type: 'siren', dur: AMBU.t1 - AMBU.t0 - 1, pan: [-0.8, 0.8], gain: 0.3 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    prerender();
    FLAP_C = renderFlap();
    return { render: renderFrame };
  },
};
